import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstantTheme } from '../../contexts/InstantThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Assessment,
  Print,
  FileDownload,
  Refresh,
  Close,
  Logout,
  Delete,
  PictureAsPdf,
  TrendingUp,
  ReceiptLong,
  LocalPrintshop,
  CalendarMonth,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
} from 'recharts';
import DailyClosingReportsService, { DailyClosingReport, Zwischenbericht } from '../../services/dailyClosingReportsService';
import { supabase } from '../../services/supabaseClient';
import LogoCacheService from '../../services/logoCacheService';

// Normalize a report to ensure all nested fields exist (prevents crashes on old/auto-generated reports)
const normalizeReport = (report: DailyClosingReport): DailyClosingReport => {
  const fs = report.financial_summary || {} as any;
  const pm = report.payment_methods || {} as any;
  const cr = report.cash_reconciliation || {} as any;
  const sa = report.sales_analysis || {} as any;
  const om = report.operational_metrics || {} as any;

  const normPm = (m: any) => ({
    count: m?.count ?? 0,
    amount: m?.amount ?? 0,
    percentage: m?.percentage ?? 0,
  });

  return {
    ...report,
    branch: report.branch || 'saheeh-riyadh',
    shift_start: report.shift_start || new Date(report.report_date + 'T00:00:00').toISOString(),
    shift_end: report.shift_end || new Date(report.report_date + 'T23:59:59').toISOString(),
    financial_summary: {
      total_sales: fs.total_sales ?? 0,
      total_orders: fs.total_orders ?? 0,
      total_customers: fs.total_customers ?? 0,
      average_order_value: fs.average_order_value ?? 0,
      gross_revenue: fs.gross_revenue ?? 0,
      net_revenue: fs.net_revenue ?? 0,
      tax_collected: fs.tax_collected ?? 0,
      discounts_given: fs.discounts_given ?? 0,
      refunds_issued: fs.refunds_issued ?? 0,
      split_payment_orders: fs.split_payment_orders ?? 0,
      split_payment_revenue: fs.split_payment_revenue ?? 0,
      split_payment_percentage: fs.split_payment_percentage ?? 0,
    },
    payment_methods: {
      cash: normPm(pm.cash),
      card: normPm(pm.card),
      paypal: normPm(pm.paypal),
      other: normPm(pm.other),
    },
    cash_reconciliation: {
      opening_cash: cr.opening_cash ?? 0,
      cash_sales: cr.cash_sales ?? 0,
      cash_refunds: cr.cash_refunds ?? 0,
      expected_cash: cr.expected_cash ?? 0,
      actual_cash: cr.actual_cash ?? 0,
      cash_difference: cr.cash_difference ?? 0,
      cash_over_short: cr.cash_over_short ?? 0,
    },
    sales_analysis: {
      hourly_breakdown: sa.hourly_breakdown || [],
      top_selling_items: sa.top_selling_items || [],
      category_performance: sa.category_performance || [],
    },
    operational_metrics: {
      peak_hours: om.peak_hours || [],
      slow_hours: om.slow_hours || [],
      busiest_staff_member: om.busiest_staff_member || '',
      average_service_time: om.average_service_time ?? 0,
      customer_satisfaction_score: om.customer_satisfaction_score ?? 0,
    },
    employee_performance: report.employee_performance || [],
    audit_trail: report.audit_trail || [],
  };
};

const DailyClosingReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { appTheme } = useInstantTheme();
  const { t, language } = useLanguage();

  // Pre-load logo when component mounts to avoid delays during PDF export
  useEffect(() => {
    LogoCacheService.getLogo().catch(error => {
      console.warn('⚠️ [DAILY CLOSING] Failed to pre-load logo:', error);
    });
  }, []);

  // State management
  const [dailyReports, setDailyReports] = useState<DailyClosingReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [generateReportDialog, setGenerateReportDialog] = useState(false);
  const [viewReportDialog, setViewReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<DailyClosingReport | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [resetTargetReport, setResetTargetReport] = useState<DailyClosingReport | null>(null);
  const [printPreviewDialog, setPrintPreviewDialog] = useState(false);
  const [previewReport, setPreviewReport] = useState<DailyClosingReport | null>(null);
  const [zwischenberichtPreviewDialog, setZwischenberichtPreviewDialog] = useState(false);
  const [previewZwischenbericht, setPreviewZwischenbericht] = useState<Zwischenbericht | null>(null);
  const [generatedZwischenbericht, setGeneratedZwischenbericht] = useState<Zwischenbericht | null>(null);

  // Current logged-in user info
  const [currentUserInfo, setCurrentUserInfo] = useState<{ name: string; email: string } | null>(null);

  // Form states
  const [reportForm, setReportForm] = useState({
    reportDate: new Date().toISOString().split('T')[0],
    cashierName: '',
    openingCash: 0,
    actualCash: 0,
  });

  const reportsService = DailyClosingReportsService.getInstance();

  // Get current logged-in user info
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          let name = '';
          let email = user.email || '';
          // Pull friendly name from staff_users (RLS-scoped to tenant)
          const { data: staff } = await supabase
            .from('staff_users')
            .select('full_name, email')
            .eq('auth_user_id', user.id)
            .maybeSingle();
          if (staff) {
            name = (staff as any).full_name || '';
            email = (staff as any).email || email;
          }
          setCurrentUserInfo({
            name: name || email || 'Staff Member',
            email,
          });
          const cashierIdentifier = email || name || 'Staff Member';
          setReportForm(prev => ({ ...prev, cashierName: cashierIdentifier }));
          console.log(`✅ [DAILY CLOSING] Set cashier name to: "${cashierIdentifier}"`);
        }
      } catch (error) {
        console.error('❌ [DAILY CLOSING] Failed to fetch current user:', error);
        try {
          const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}');
          if (u.email) {
            setCurrentUserInfo({ name: u.name || u.email, email: u.email });
            setReportForm(prev => ({ ...prev, cashierName: u.email }));
          }
        } catch { /* ignore */ }
      }
    };

    fetchCurrentUser();
  }, []);


  // Load data on component mount and clean up old data
  useEffect(() => {
    // Clean up old max.fischer data on mount
    const cleanupOldData = async () => {
      try {
        const deletedCount = await reportsService.deleteReportsByCashier('max.fischer@saheehpos.com');
        if (deletedCount > 0) {
          console.log(`✅ [DAILY CLOSING] Cleaned up ${deletedCount} old reports for max.fischer@saheehpos.com`);
        }
      } catch (error) {
        console.error('❌ [DAILY CLOSING] Failed to cleanup old data:', error);
      }
    };

    cleanupOldData();
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 [DAILY CLOSING] Loading daily closing reports...');

      const reports = await reportsService.getDailyClosingReports();

      // Filter out old test data (max.fischer@saheehpos.com) and only exclude obvious test/mock data
      const filteredReports = reports.filter(report => {
        if (!report.cashier_name) {
          return false; // Exclude reports without cashier name
        }

        const cashierLower = report.cashier_name.toLowerCase();

        // Exclude old test data
        if (cashierLower.includes('max.fischer@saheehpos.com') ||
          cashierLower.includes('max.fischer') ||
          cashierLower === 'max.fischer@saheehpos.com') {
          return false;
        }

        // Exclude obvious test/mock patterns
        const excludePatterns = [
          /^test\d*$/i,
          /^mock\d*$/i,
          /^dummy\d*$/i,
          /^user\d+$/i,
        ];

        const isTestData = excludePatterns.some(pattern =>
          pattern.test(cashierLower)
        );

        return !isTestData;
      });

      setDailyReports(filteredReports);

      console.log(`✅ [DAILY CLOSING] Loaded ${filteredReports.length} daily closing reports (filtered from ${reports.length} total)`);

    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to load data:', error);
      setError(t('daily_closing.delete_error'));
    } finally {
      setLoading(false);
    }
  };

  const generateDailyReport = async () => {
    try {
      setLoading(true);

      console.log('📊 [DAILY CLOSING] Generating daily closing report...');

      const report = await reportsService.generateDailyClosingReport(
        reportForm.reportDate,
        reportForm.cashierName,
        reportForm.openingCash,
        reportForm.actualCash
      );

      setDailyReports(prev => [report, ...prev]);
      setGenerateReportDialog(false);

      console.log('✅ [DAILY CLOSING] Daily closing report generated successfully');

    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to generate daily closing report:', error);
      setError(t('daily_closing.interim_error'));
    } finally {
      setLoading(false);
    }
  };

  const generateInterimSummary = async () => {
    try {
      setLoading(true);
      console.log('📊 [INTERIM SALES] Generating interim sales summary...');

      const summary = await reportsService.generateInterimSalesSummary(
        reportForm.cashierName || 'Current Cashier'
      );

      console.log('✅ [INTERIM SALES] Interim sales summary generated successfully', summary);

      // Show success message with summary details
      alert(`✅ ${t('daily_closing.interim_success')}\n\n` +
        `${t('daily_closing.current_sales')}: €${summary.quick_summary.current_sales.toFixed(2)}\n` +
        `${t('daily_closing.current_orders')}: ${summary.quick_summary.current_orders}\n` +
        `${t('daily_closing.hourly_rate')}: €${summary.quick_summary.hourly_rate.toFixed(2)}/hour\n` +
        `${t('daily_closing.projected_daily_sales')}: €${summary.quick_summary.projected_daily_sales.toFixed(2)}\n\n` +
        `${t('daily_closing.summary_saved')}`);

    } catch (error) {
      console.error('❌ [INTERIM SALES] Failed to generate interim sales summary:', error);
      setError(t('daily_closing.interim_error'));
      alert('❌ ' + t('daily_closing.interim_error'));
    } finally {
      setLoading(false);
    }
  };

  const generateZwischenbericht = async (showPreview: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      console.log('📊 [ZWISCHENBERICHT] Generating Zwischenbericht...');
      
      const zwischenbericht = await reportsService.generateZwischenbericht(
        reportForm.reportDate || new Date().toISOString().split('T')[0],
        reportForm.cashierName || currentUserInfo?.name || 'Staff Member',
        'Kasse 1'
      );
      
      console.log('✅ [ZWISCHENBERICHT] Zwischenbericht generated successfully', zwischenbericht);
      
      // Save to state for PDF export
      setGeneratedZwischenbericht(zwischenbericht);
      
      if (showPreview) {
        // Show preview
        setPreviewZwischenbericht(zwischenbericht);
        setZwischenberichtPreviewDialog(true);
      } else {
        // Print directly
        await printZwischenbericht(zwischenbericht);
      }
      
    } catch (error: any) {
      console.error('❌ [ZWISCHENBERICHT] Failed to generate Zwischenbericht:', error);
      setError(t('daily_closing.interim_error'));
      alert('❌ ' + t('daily_closing.interim_error'));
    } finally {
      setLoading(false);
    }
  };

  const printZwischenbericht = async (zwischenbericht: Zwischenbericht) => {
    try {
      // Generate print content
      const printContent = generateZwischenberichtPrintContent(zwischenbericht);
      
      // EXTENSIVE DEBUG LOGGING FOR LOGO
      console.log('🔍 [ZWISCHENBERICHT DEBUG] ========================================');
      console.log('🔍 [ZWISCHENBERICHT DEBUG] Generated print content length:', printContent.length);
      console.log('🔍 [ZWISCHENBERICHT DEBUG] Has [LOGO_PLACEHOLDER]:', printContent.includes('[LOGO_PLACEHOLDER]'));
      console.log('🔍 [ZWISCHENBERICHT DEBUG] Logo placeholder position:', printContent.indexOf('[LOGO_PLACEHOLDER]'));
      console.log('🔍 [ZWISCHENBERICHT DEBUG] First 500 chars of content:', printContent.substring(0, 500));
      console.log('🔍 [ZWISCHENBERICHT DEBUG] Content around logo placeholder:', 
        printContent.substring(Math.max(0, printContent.indexOf('[LOGO_PLACEHOLDER]') - 50), 
        Math.min(printContent.length, printContent.indexOf('[LOGO_PLACEHOLDER]') + 100)));
      console.log('🔍 [ZWISCHENBERICHT DEBUG] ========================================');
      
      // Try to use print service
      try {
        const { localPrintServerService } = await import('../../services/localPrintServerService');
        
        // Check if print server is running
        const serverRunning = await localPrintServerService.checkServerStatus();
        if (serverRunning) {
          // Print to BOTH cashier printers (Epson and POS-80C Kasse)
          const cashierPrinters = [
            { ip: '192.168.178.46', name: 'Epson TM-m30II' },
            { ip: '192.168.178.45', name: 'POS-80C Kasse' }
          ];
          
          console.log('🖨️ [ZWISCHENBERICHT] Printing to BOTH cashier printers...');
          const printPromises = cashierPrinters.map(printer => {
            console.log(`🖨️ [ZWISCHENBERICHT] Sending to ${printer.name} (${printer.ip}:9100)...`);
            return localPrintServerService.printReceipt(printer.ip, 9100, printContent)
              .then(result => {
                if (result.success) {
                  console.log(`✅ [ZWISCHENBERICHT] Successfully printed to ${printer.name}`);
                } else {
                  console.error(`❌ [ZWISCHENBERICHT] Failed to print to ${printer.name}:`, result.message);
                }
                return { printer: printer.name, success: result.success, message: result.message };
              })
              .catch(error => {
                console.error(`❌ [ZWISCHENBERICHT] Error printing to ${printer.name}:`, error);
                return { printer: printer.name, success: false, message: error.message };
              });
          });
          
          const results = await Promise.all(printPromises);
          const successCount = results.filter(r => r.success).length;
          
          if (successCount > 0) {
            console.log(`✅ [ZWISCHENBERICHT] Printed to ${successCount}/${cashierPrinters.length} printers`);
            alert('✅ ' + t('daily_closing.print_interim_success').replace('{success}', String(successCount)).replace('{total}', String(cashierPrinters.length)));
            return;
          } else {
            console.error('❌ [ZWISCHENBERICHT] Failed to print to all printers');
            // Fall through to browser print
          }
        } else {
          console.warn('⚠️ [ZWISCHENBERICHT] Print server not running, using browser print');
        }
      } catch (printError) {
        console.warn('⚠️ [ZWISCHENBERICHT] Print service error, using browser print:', printError);
      }
      
      // Fallback: Use browser print with clean formatted text (no ESC/POS commands)
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        // Use the clean preview text instead of trying to clean ESC/POS commands
        const cleanContent = generateZwischenberichtPreviewText(zwischenbericht);
        
        printWindow.document.write(`
          <html>
            <head>
              <title>Zwischenbericht</title>
              <style>
                @media print {
                  @page {
                    size: 80mm auto;
                    margin: 5mm;
                  }
                }
                body {
                  font-family: 'Courier New', monospace;
                  font-size: 16px;
                  padding: 15px;
                  white-space: pre-wrap;
                  line-height: 1.8;
                  max-width: 350px;
                  margin: 0 auto;
                }
                .center { text-align: center; }
                .bold { font-weight: bold; font-size: 18px; }
                h1 { font-size: 24px; font-weight: bold; }
              </style>
            </head>
            <body>${cleanContent.split('\n').map(line => {
              const isBold = line.includes('KASSENABSCHLUSSBELEG') ||
                           line.includes('BAR ZAHLUNGEN') ||
                           line.includes('ALLE ZAHLUNGEN') ||
                           line.includes('BONUSSYSTEM') ||
                           (line.includes('Umsatz') && line.includes('%')) ||
                           line.includes('Vorbestand') ||
                           line.includes('Einnahmen') ||
                           line.includes('Ausgaben') ||
                           line.includes('Endbestand') ||
                           line.includes('Gesamt');
              const isCenter = line.includes('[LOGO]') ||
                              line.includes('KASSENABSCHLUSSBELEG') ||
                              line.includes('Äußere Kanalstraße') ||
                              line.includes('Steuernummer') ||
                              line.trim() === '50827 Köln' ||
                              line === '50827 Köln';
              const className = (isBold ? 'bold ' : '') + (isCenter ? 'center' : '');
              return `<div class="${className}">${line || ' '}</div>`;
            }).join('')}</body>
          </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      } else {
        alert('❌ ' + t('daily_closing.popup_blocked'));
      }
      
    } catch (error: any) {
      console.error('❌ [ZWISCHENBERICHT] Failed to print:', error);
      alert('❌ ' + t('daily_closing.print_interim_error') + ': ' + (error.message || t('payments.unknown')));
    }
  };

  const exportZwischenberichtToPDF = async (zwischenbericht: Zwischenbericht) => {
    try {
      console.log('📄 [ZWISCHENBERICHT PDF] Exporting Zwischenbericht to PDF...');
      
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 297] // Thermal printer width format
      });
      
      // Load PNG logo - prioritize local file (no CORS), fallback to cache service
      console.log('🖼️ [ZWISCHENBERICHT PDF] Loading PNG logo...');

      let logoData: string | null = null;
      let logoAdded = false;

      // Method 1: Try loading from local public folder first (same origin, no CORS)
      try {
        const localLogoUrl = '/saheeh_logo_w.svg';
        console.log('🖼️ [ZWISCHENBERICHT PDF] Attempting to load logo from local file:', localLogoUrl);

        const localResponse = await fetch(localLogoUrl, { cache: 'no-cache' });

        if (localResponse.ok) {
          const blob = await localResponse.blob();
          console.log('📦 [ZWISCHENBERICHT PDF] Logo blob received:', { size: blob.size, type: blob.type });

          if (blob.size === 0) {
            throw new Error('Logo blob is empty');
          }

          logoData = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result && typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
                console.log('✅ [ZWISCHENBERICHT PDF] Logo loaded from local file (no CORS issues), length:', reader.result.length);
                resolve(reader.result);
              } else {
                reject(new Error('Invalid logo data from local file'));
              }
            };
            reader.onerror = () => reject(new Error('FileReader error'));
            reader.readAsDataURL(blob);
          });
        } else {
          throw new Error(`Local logo not found: ${localResponse.status} ${localResponse.statusText}`);
        }
      } catch (localError) {
        console.warn('⚠️ [ZWISCHENBERICHT PDF] Local logo not available, trying LogoCacheService:', localError);

        // Method 2: Use LogoCacheService to load from Firebase Storage (handles CORS via Image element)
        try {
          logoData = await LogoCacheService.getLogo();
          if (logoData && logoData.length > 100) {
            console.log('✅ [ZWISCHENBERICHT PDF] Logo loaded from LogoCacheService, length:', logoData.length);
          } else {
            throw new Error('LogoCacheService returned invalid data');
          }
        } catch (cacheError) {
          console.error('❌ [ZWISCHENBERICHT PDF] Failed to load logo from LogoCacheService:', cacheError);
          logoData = null;
        }
      }
      
      let yPos = 5;
      const margin = 5;
      const pageWidth = 80;

      // Add app logo at the top (centered)
      if (logoData && logoData.length > 100 && logoData.startsWith('data:image/')) {
        try {
          let actualFormat: 'PNG' | 'JPEG' = 'PNG';
          if (logoData.startsWith('data:image/jpeg') || logoData.startsWith('data:image/jpg')) {
            actualFormat = 'JPEG';
          }
          // Logo actual pixels: 4520x3508 → ratio 1.288:1
          const logoWidth = 22;
          const logoHeight = logoWidth / 1.288; // ~17mm, correct aspect ratio
          const xPos = (pageWidth - logoWidth) / 2;
          doc.addImage(logoData, actualFormat, xPos, yPos, logoWidth, logoHeight);
          logoAdded = true;
          yPos += logoHeight + 2;
        } catch (imgError) {
          console.error('❌ [ZWISCHENBERICHT PDF] Failed to add logo:', imgError);
          logoAdded = false;
        }
      }

      // Helper function to add two-column text (label left, value right)
      const addTwoColumn = (label: string, value: string, fontSize: number = 9, labelBold: boolean = false) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', labelBold ? 'bold' : 'normal');
        doc.text(label, margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(value, pageWidth - margin, yPos, { align: 'right' });
        yPos += fontSize * 0.4 + 2;
      };

      // Fallback text if no logo
      if (!logoAdded) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('SAHEEH', pageWidth / 2, yPos + 5, { align: 'center' });
        yPos += 12;
      }

      // Address (Centered)
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text('Äußere Kanalstraße 211', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      doc.text('50827 Köln', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      doc.text('Steuernummer: 217/5786/1785', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      
      // Separator line
      doc.setLineWidth(0.3);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // Report Number and Details (Left-aligned, Normal font)
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const reportNumber = zwischenbericht.id ? parseInt(zwischenbericht.id.substring(0, 8), 16) % 10000 : Math.floor(Math.random() * 10000);
      doc.text(`Nummer: ${reportNumber}`, margin, yPos);
      yPos += 5;
      
      // Date and Time
      const reportDate = new Date(zwischenbericht.report_date);
      const dateStr = reportDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
      doc.text(`Datum: ${dateStr}`, margin, yPos);
      yPos += 5;
      doc.text(`Durchgeführt am ${dateStr} ${zwischenbericht.report_time}`, margin, yPos);
      yPos += 5;
      doc.text(`Durchgeführt an ${zwischenbericht.cash_register}`, margin, yPos);
      yPos += 5;
      doc.text(`Benutzer: ${zwischenbericht.cashier_name}`, margin, yPos);
      yPos += 5;
      doc.text('Bemerkung:', margin, yPos);
      yPos += 6;
      
      // Separator line
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // Financial Summary - Two column format (matching reference exactly)
      doc.setFontSize(9);
      addTwoColumn('Vorbestand:', `${zwischenbericht.financial_summary.total_expenses.toFixed(2).replace('.', ',')}€`, 9, true);
      addTwoColumn('Einnahmen:', `${zwischenbericht.financial_summary.total_income.toFixed(2).replace('.', ',')}€`, 9, true);
      addTwoColumn('Ausgaben:', `${zwischenbericht.financial_summary.total_expenses.toFixed(2).replace('.', ',')}€`, 9, true);
      addTwoColumn('Endbestand:', `${zwischenbericht.financial_summary.final_balance.toFixed(2).replace('.', ',')}€`, 9, true);
      yPos += 3;
      
      // Separator line
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // EINNAHMEN Section Header (Bold, Larger)
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('EINNAHMEN', margin, yPos);
      yPos += 6;
      
      // VAT Breakdown Table - Professional format
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      // Table header
      const headerText = 'Bez.'.padEnd(15) + 'Steuer'.padStart(8) + 'Betrag'.padStart(12);
      doc.text(headerText, margin, yPos);
      yPos += 5;
      
      doc.setFont('helvetica', 'normal');
      // VAT 7.00% row
      const vat7Gross = zwischenbericht.vat_breakdown.vat_7_percent.gross.toFixed(2).replace('.', ',');
      const vat7Row = '7,00%'.padEnd(15) + '7,00%'.padStart(8) + (vat7Gross + '€').padStart(12);
      doc.text(vat7Row, margin, yPos);
      yPos += 5;
      doc.text(`N: ${zwischenbericht.vat_breakdown.vat_7_percent.net.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
      yPos += 4;
      doc.text(`S: ${zwischenbericht.vat_breakdown.vat_7_percent.tax.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
      yPos += 5;
      
      // VAT 19.00% row
      const vat19Gross = zwischenbericht.vat_breakdown.vat_19_percent.gross.toFixed(2).replace('.', ',');
      const vat19Row = '19,00%'.padEnd(15) + '19,00%'.padStart(8) + (vat19Gross + '€').padStart(12);
      doc.text(vat19Row, margin, yPos);
      yPos += 5;
      doc.text(`N: ${zwischenbericht.vat_breakdown.vat_19_percent.net.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
      yPos += 4;
      doc.text(`S: ${zwischenbericht.vat_breakdown.vat_19_percent.tax.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
      yPos += 5;
      
      // Totals
      doc.setFont('helvetica', 'bold');
      addTwoColumn('Gesamt Netto:', `${zwischenbericht.vat_breakdown.total_net.toFixed(2).replace('.', ',')}€`, 9, true);
      addTwoColumn('Gesamt:', `${zwischenbericht.vat_breakdown.total_gross.toFixed(2).replace('.', ',')}€`, 9, true);
      yPos += 3;
      
      // Separator
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // KASSENVORGÄNGE Section (if applicable)
      if (zwischenbericht.cash_transactions.length > 0 || zwischenbericht.payment_methods.cash.amount > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('KASSENVORGÄNGE', margin, yPos);
        yPos += 6;
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        zwischenbericht.cash_transactions.forEach(transaction => {
          doc.text(`Bezeichnung: ${transaction.type}`, margin + 2, yPos);
          yPos += 4;
          doc.text(`Betrag: ${transaction.amount.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
          yPos += 5;
        });
        if (zwischenbericht.cash_transactions.length === 0 && zwischenbericht.payment_methods.cash.amount > 0) {
          doc.text('Bezeichnung: Bankeinzahlung', margin + 2, yPos);
          yPos += 4;
          doc.text(`Betrag: ${zwischenbericht.payment_methods.cash.amount.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
          yPos += 5;
        }
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      }
      
      // ZAHLARTEN Section - Professional table format
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('ZAHLARTEN', margin, yPos);
      yPos += 6;
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      // Table header
      const zahlartHeader = 'Zahlart'.padEnd(30) + 'Betrag'.padStart(15);
      doc.text(zahlartHeader, margin, yPos);
      yPos += 5;
      
      doc.setFont('helvetica', 'normal');
      // Payment methods rows
      if (zwischenbericht.payment_methods.cash.count > 0) {
        const cashRow = 'bar'.padEnd(30) + (zwischenbericht.payment_methods.cash.amount.toFixed(2).replace('.', ',') + '€').padStart(15);
        doc.text(cashRow, margin, yPos);
        yPos += 5;
      }
      if (zwischenbericht.payment_methods.takeaway_com.count > 0) {
        const takeawayRow = 'Onlinezahlung takeaway.com'.padEnd(30) + (zwischenbericht.payment_methods.takeaway_com.amount.toFixed(2).replace('.', ',') + '€').padStart(15);
        doc.text(takeawayRow, margin, yPos);
        yPos += 5;
      }
      if (zwischenbericht.payment_methods.ubereats.count > 0) {
        const uberRow = 'Onlinezahlung UberEats'.padEnd(30) + (zwischenbericht.payment_methods.ubereats.amount.toFixed(2).replace('.', ',') + '€').padStart(15);
        doc.text(uberRow, margin, yPos);
        yPos += 5;
      }
      yPos += 2;
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // BAR ZAHLUNGEN NACH VERKAUFSKANAL Section
      if (zwischenbericht.sales_channels_cash.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('BAR ZAHLUNGEN NACH VERKAUFSKANAL', margin, yPos);
        yPos += 6;
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        zwischenbericht.sales_channels_cash.forEach(channel => {
          doc.text(`Bezeichnung: ${channel.channel}`, margin + 2, yPos);
          yPos += 4;
          doc.text(`Betrag: ${channel.amount.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
          yPos += 5;
        });
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      }
      
      // ALLE ZAHLUNGEN NACH VERKAUFSKANAL Section
      if (zwischenbericht.sales_channels_all.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('ALLE ZAHLUNGEN NACH VERKAUFSKANAL', margin, yPos);
        yPos += 6;
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        zwischenbericht.sales_channels_all.forEach(channel => {
          const count = channel.count || 1;
          doc.text(`${count} x ${channel.channel} - ${channel.payment_method}`, margin + 2, yPos);
          yPos += 4;
          doc.text(`Betrag: ${channel.amount.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
          yPos += 5;
        });
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      }
      
      // BONUSSYSTEM Section
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('BONUSSYSTEM', margin, yPos);
      yPos += 6;
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Vergebene Punkte:', margin + 2, yPos);
      yPos += 4;
      doc.text(`Wert vergebener Punkte: ${zwischenbericht.bonus_system.amount.toFixed(2).replace('.', ',')}€`, margin + 2, yPos);
      yPos += 5;
      doc.text('Eingelöste Punkte:', margin + 2, yPos);
      yPos += 4;
      doc.text('Wert eingelöster Punkte: 0,00€', margin + 2, yPos);
      yPos += 8;
      
      // Footer - KASSENABSCHLUSSBELEG (Large, Bold, Centered)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('KASSENABSCHLUSSBELEG', pageWidth / 2, yPos, { align: 'center' });
      
      // Save PDF
      const fileName = `Zwischenbericht_${zwischenbericht.report_date}_${zwischenbericht.cashier_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);
      
      console.log('✅ [ZWISCHENBERICHT PDF] PDF exported successfully');
      
    } catch (error) {
      console.error('❌ [ZWISCHENBERICHT PDF] Failed to export PDF:', error);
      alert(t('daily_closing.export_error'));
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!reportId) {
      alert('❌ ' + t('daily_closing.id_missing'));
      return;
    }

    const confirmDelete = window.confirm(
      t('daily_closing.delete_confirm')
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      console.log(`🗑️ [DAILY CLOSING] Deleting report: ${reportId}`);

      await reportsService.deleteDailyClosingReport(reportId);

      // Remove from local state
      setDailyReports(prev => prev.filter(report => report.id !== reportId));

      console.log('✅ [DAILY CLOSING] Report deleted successfully');
      alert(t('daily_closing.delete_success'));

    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to delete report:', error);
      setError(t('daily_closing.delete_error'));
      alert(t('daily_closing.delete_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResetDialog = (report: DailyClosingReport) => {
    setResetTargetReport(report);
    setResetReason('');
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!resetTargetReport || !resetTargetReport.id) {
      alert('❌ ' + t('daily_closing.info_missing'));
      return;
    }

    if (!resetReason.trim()) {
      alert(t('daily_closing.reset_reason_required'));
      return;
    }

    try {
      setLoading(true);
      console.log(`♻️ [DAILY CLOSING] Resetting report: ${resetTargetReport.id}`);

      const performer = {
        name: currentUserInfo?.name || reportForm.cashierName || t('daily_closing.unknown_staff'),
        email: currentUserInfo?.email,
      };

      await reportsService.resetDailyClosingReport(resetTargetReport.id, resetReason.trim(), performer);

      // GoBD: mirror the reset into the main audit log so a Finanzamt
      // auditor sees the event in the same trail as order changes.
      // (resetDailyClosingReport itself writes to a separate
      // daily_closing_resets collection — that's necessary internal state
      // but invisible to a reviewer scanning order_audit_log.)
      try {
        const { HAS_VALID_FIREBASE_CONFIG } = await import('../../services/firebaseConfig');
        if (!HAS_VALID_FIREBASE_CONFIG) throw new Error('Firebase Functions not configured');
        const { httpsCallable, getFunctions } = await import('firebase/functions');
        const fns = getFunctions();
        const callable = httpsCallable(fns, 'logAuditEvent');
        await callable({
          action: 'daily_closing_reset',
          target: resetTargetReport.id,
          reason: resetReason.trim(),
          metadata: {
            report_date: resetTargetReport.report_date,
            cashier_name: resetTargetReport.cashier_name,
            performed_by_name: performer.name,
            performed_by_email: performer.email || null,
          },
        });
      } catch (auditErr) {
        console.warn('[AUDIT] Failed to log daily_closing_reset event (non-fatal):', auditErr);
      }

      // Remove from local state
      setDailyReports(prev => prev.filter(report => report.id !== resetTargetReport.id));

      setResetDialogOpen(false);
      setResetTargetReport(null);
      setResetReason('');

      alert(t('daily_closing.reset_success'));
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to reset report:', error);
      setError(t('daily_closing.reset_error'));
      alert(t('daily_closing.reset_error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPreview = (report: DailyClosingReport) => {
    setPreviewReport(normalizeReport(report));
    setPrintPreviewDialog(true);
  };

  const printReport = async (report: DailyClosingReport) => {
    try {
      console.log('🖨️ [PRINT] Printing daily closing report...');

      // Import print service
      const { localPrintServerService } = await import('../../services/localPrintServerService');

      // Check if print server is running
      const serverRunning = await localPrintServerService.checkServerStatus();
      if (!serverRunning) {
        alert(t('daily_closing.print_server_not_running'));
        return;
      }

      // Generate print content with selected language and app logo
      const printContent = generatePrintContent(report, language);

      console.log('🖨️ [PRINT] Generated print content length:', printContent.length);
      console.log('🖨️ [PRINT] Print content preview:', printContent.substring(0, 200) + '...');

      // Print to cash printer with proper parameters
      const result = await localPrintServerService.printReceipt('192.168.178.46', 9100, printContent);

      if (result.success) {
        console.log('✅ [PRINT] Daily closing report printed successfully');
        alert(t('daily_closing.print_success'));
        setPrintPreviewDialog(false);
      } else {
        console.error('❌ [PRINT] Failed to print daily closing report:', result.message);
        alert(t('daily_closing.print_error') + ': ' + result.message);
      }

    } catch (error) {
      console.error('❌ [PRINT] Error printing daily closing report:', error);
      alert(t('daily_closing.print_error'));
    }
  };

  // Generate formatted preview text (for dialog display) - matches PDF format exactly
  const generateZwischenberichtPreviewText = (report: Zwischenbericht): string => {
    const lines: string[] = [];
    
    // Logo placeholder (centered) - will show as text in preview
    // Only logo, no business name text
    lines.push('[LOGO]');
    lines.push('');
    lines.push('');
    
    // Business Address (centered) - CSS will center these, no manual padding
    lines.push('Äußere Kanalstraße 211');
    lines.push('50827 Köln');
    lines.push('Steuernummer: 217/5786/1785');
    lines.push('────────────────────────────────');
    
    // Report Details
    const reportNumber = report.id ? parseInt(report.id.substring(0, 8), 16) % 10000 : Math.floor(Math.random() * 10000);
    const reportDate = new Date(report.report_date);
    const dateStr = reportDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    lines.push(`Nummer: ${reportNumber}`);
    lines.push(`Datum: ${dateStr}`);
    lines.push(`Durchgeführt am ${dateStr} ${report.report_time}`);
    lines.push(`Durchgeführt an ${report.cash_register}`);
    lines.push(`Benutzer: ${report.cashier_name}`);
    lines.push('Bemerkung:');
    lines.push('────────────────────────────────');
    
      // Financial Summary - Two column format (values right-aligned at column 48)
      const vorbestand = report.financial_summary.total_expenses.toFixed(2).replace('.', ',');
      const einnahmen = report.financial_summary.total_income.toFixed(2).replace('.', ',');
      const ausgaben = report.financial_summary.total_expenses.toFixed(2).replace('.', ',');
      const endbestand = report.financial_summary.final_balance.toFixed(2).replace('.', ',');
      
      const RECEIPT_WIDTH_PREVIEW = 48;
      
      const vorbestandLabel = 'Vorbestand:';
      const vorbestandValue = vorbestand + '€';
      const vorbestandSpaces = RECEIPT_WIDTH_PREVIEW - vorbestandLabel.length - vorbestandValue.length;
      
      const einnahmenLabel = 'Einnahmen:';
      const einnahmenValue = einnahmen + '€';
      const einnahmenSpaces = RECEIPT_WIDTH_PREVIEW - einnahmenLabel.length - einnahmenValue.length;
      
      const ausgabenLabel = 'Ausgaben:';
      const ausgabenValue = ausgaben + '€';
      const ausgabenSpaces = RECEIPT_WIDTH_PREVIEW - ausgabenLabel.length - ausgabenValue.length;
      
      const endbestandLabel = 'Endbestand:';
      const endbestandValue = endbestand + '€';
      const endbestandSpaces = RECEIPT_WIDTH_PREVIEW - endbestandLabel.length - endbestandValue.length;
      
      lines.push('Vorbestand:' + ' '.repeat(vorbestandSpaces) + vorbestandValue);
      lines.push('Einnahmen:' + ' '.repeat(einnahmenSpaces) + einnahmenValue);
      lines.push('Ausgaben:' + ' '.repeat(ausgabenSpaces) + ausgabenValue);
      lines.push('Endbestand:' + ' '.repeat(endbestandSpaces) + endbestandValue);
    lines.push('────────────────────────────────');
    
    // EINNAHMEN Section
    lines.push('EINNAHMEN');
    lines.push('');
    lines.push('Bez.'.padEnd(20) + 'Steuer'.padStart(10) + 'Betrag'.padStart(12));
    
    const vat7Gross = report.vat_breakdown.vat_7_percent.gross.toFixed(2).replace('.', ',');
    const vat7Net = report.vat_breakdown.vat_7_percent.net.toFixed(2).replace('.', ',');
    const vat7Tax = report.vat_breakdown.vat_7_percent.tax.toFixed(2).replace('.', ',');
    lines.push('7,00%'.padEnd(20) + '7,00%'.padStart(10) + (vat7Gross + '€').padStart(12));
    lines.push(`  N: ${vat7Net}€ S: ${vat7Tax}€`);
    
    lines.push('────────────────────────────────');
    
    const vat19Gross = report.vat_breakdown.vat_19_percent.gross.toFixed(2).replace('.', ',');
    const vat19Net = report.vat_breakdown.vat_19_percent.net.toFixed(2).replace('.', ',');
    const vat19Tax = report.vat_breakdown.vat_19_percent.tax.toFixed(2).replace('.', ',');
    lines.push('19,00%'.padEnd(20) + '19,00%'.padStart(10) + (vat19Gross + '€').padStart(12));
    lines.push(`  N: ${vat19Net}€ S: ${vat19Tax}€`);
    
      const totalNet = report.vat_breakdown.total_net.toFixed(2).replace('.', ',');
      const totalGross = report.vat_breakdown.total_gross.toFixed(2).replace('.', ',');
      lines.push('');
      
      // Right-align values at column 48
      const gesamtNettoLabel = 'Gesamt Netto:';
      const gesamtNettoValue = totalNet + '€';
      const gesamtNettoSpaces = RECEIPT_WIDTH_PREVIEW - gesamtNettoLabel.length - gesamtNettoValue.length;
      
      const gesamtLabel = 'Gesamt:';
      const gesamtValue = totalGross + '€';
      const gesamtSpaces = RECEIPT_WIDTH_PREVIEW - gesamtLabel.length - gesamtValue.length;
      
      lines.push('Gesamt Netto:' + ' '.repeat(gesamtNettoSpaces) + gesamtNettoValue);
      lines.push('Gesamt:' + ' '.repeat(gesamtSpaces) + gesamtValue);
    lines.push('────────────────────────────────');
    
    // KASSENVORGÄNGE Section
    if (report.cash_transactions.length > 0 || report.payment_methods.cash.amount > 0) {
      lines.push('KASSENVORGÄNGE');
      lines.push('');
      if (report.cash_transactions.length > 0) {
        report.cash_transactions.forEach(transaction => {
          lines.push(`Bezeichnung: ${transaction.type}`);
          lines.push(`Betrag: ${transaction.amount.toFixed(2).replace('.', ',')}€`);
        });
      } else if (report.payment_methods.cash.amount > 0) {
        lines.push('Bezeichnung: Bankeinzahlung');
        lines.push(`Betrag: ${report.payment_methods.cash.amount.toFixed(2).replace('.', ',')}€`);
      }
      lines.push('────────────────────────────────');
    }
    
    // ZAHLARTEN Section
    lines.push('ZAHLARTEN');
    lines.push('');
    lines.push('Zahlart'.padEnd(35) + 'Betrag'.padStart(12));
    lines.push('');
    
    if (report.payment_methods.cash.count > 0) {
      const cashAmount = report.payment_methods.cash.amount.toFixed(2).replace('.', ',');
      lines.push('bar'.padEnd(35) + (cashAmount + '€').padStart(12));
    }
    if (report.payment_methods.takeaway_com.count > 0) {
      const takeawayAmount = report.payment_methods.takeaway_com.amount.toFixed(2).replace('.', ',');
      lines.push('Onlinezahlung takeaway.com'.padEnd(35) + (takeawayAmount + '€').padStart(12));
    }
    if (report.payment_methods.ubereats.count > 0) {
      const ubereatsAmount = report.payment_methods.ubereats.amount.toFixed(2).replace('.', ',');
      lines.push('Onlinezahlung UberEats'.padEnd(35) + (ubereatsAmount + '€').padStart(12));
    }
    lines.push('────────────────────────────────');
    
    // BAR ZAHLUNGEN NACH VERKAUFSKANAL
    if (report.sales_channels_cash.length > 0) {
      lines.push('BAR ZAHLUNGEN NACH VERKAUFSKANAL');
      lines.push('');
      report.sales_channels_cash.forEach(channel => {
        lines.push(`Bezeichnung: ${channel.channel}`);
        lines.push(`Betrag: ${channel.amount.toFixed(2).replace('.', ',')}€`);
      });
      lines.push('────────────────────────────────');
    }
    
    // ALLE ZAHLUNGEN NACH VERKAUFSKANAL
    if (report.sales_channels_all.length > 0) {
      lines.push('ALLE ZAHLUNGEN NACH VERKAUFSKANAL');
      lines.push('');
      report.sales_channels_all.forEach(channel => {
        const count = (channel as any).count || 1;
        lines.push(`${count} x ${channel.channel} - ${channel.payment_method}`);
        lines.push(`Betrag: ${channel.amount.toFixed(2).replace('.', ',')}€`);
      });
      lines.push('────────────────────────────────');
    }
    
    // BONUSSYSTEM Section
    lines.push('BONUSSYSTEM');
    lines.push('');
    lines.push('Vergebene Punkte:');
    const bonusAmount = report.bonus_system.amount.toFixed(2).replace('.', ',');
    lines.push(`Wert vergebener Punkte: ${bonusAmount}€`);
    lines.push('Eingelöste Punkte:');
    lines.push('Wert eingelöster Punkte: 0,00€');
    lines.push('');
    lines.push('');
    
    // Footer
    lines.push('KASSENABSCHLUSSBELEG');
    
    return lines.join('\n');
  };

  const generateZwischenberichtPrintContent = (report: Zwischenbericht): string => {
    const lines: string[] = [];

    // ESC/POS commands for professional thermal printing - EXACT MATCH TO DAILY CLOSING REPORT
    const ESC = '\x1B';
    const CENTER = ESC + '\x61\x01';  // Center align
    const LEFT = ESC + '\x61\x00';    // Left align
    const BOLD_ON = ESC + '\x45\x01'; // Bold on
    const BOLD_OFF = ESC + '\x45\x00'; // Bold off
    const SIZE_NORMAL = ESC + '\x21\x00'; // Normal size
    const SIZE_LARGE = ESC + '\x21\x30'; // Large size (2x width & height)
    const FEED = '\x0A'; // Line feed
    const CUT = '\x1D\x56\x00'; // Cut paper
    const INIT = '\x1B\x40'; // Initialize printer

    // Initialize printer - EXACT MATCH TO DAILY CLOSING REPORT
    lines.push(INIT);
    lines.push(FEED);

    // Set code page to support German characters (Code Page 858 for European characters)
    // Code Page 858 = Windows-1252 extended with Euro symbol - SAME AS DAILY CLOSING REPORT
    const CODE_PAGE_858 = ESC + '\x74\x13'; // ESC t 19 (Code Page 858)
    lines.push(CODE_PAGE_858);

    // LOGO AREA (will be replaced by print server) - EXACT MATCH TO DAILY CLOSING REPORT
    // The print server will inject the bitmap logo here
    lines.push(CENTER);
    lines.push('[LOGO_PLACEHOLDER]');
    lines.push(FEED);
    console.log('🔍 [DEBUG] Logo placeholder added to Zwischenbericht report');

    // NO BUSINESS NAME TEXT - Only logo should appear
    // Add spacing after logo before address
    lines.push(FEED);

    // Business Information - EXACT MATCH (with German characters)
    // Use actual German characters - they will be properly encoded when sent to printer
    // The printer will interpret them according to Code Page 858
    // All address lines centered
    lines.push(CENTER);
    lines.push('Äußere Kanalstraße 211');
    lines.push(FEED);
    lines.push(CENTER);
    lines.push('50827 Köln');
    lines.push(FEED);
    lines.push(CENTER);
    lines.push('Steuernummer: 217/5786/1785');
    lines.push(FEED);
    lines.push(LEFT); // Reset to LEFT for separator
    lines.push('--------------------------------');
    lines.push(FEED);

    // Report Details - Generate report number
    const reportNumber = report.id ? parseInt(report.id.substring(0, 8), 16) % 10000 : 
                         Math.floor(Math.random() * 10000);
    const reportDate = new Date(report.report_date);
    const dateStr = reportDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = report.report_time;
    
    lines.push(LEFT);
    lines.push(`Nummer: ${reportNumber}`);
    lines.push(`Datum: ${dateStr}`);
    lines.push(`Durchgeführt am ${dateStr} ${timeStr}`);
    lines.push(`Durchgeführt an ${report.cash_register}`);
    lines.push(`Benutzer: ${report.cashier_name}`);
    lines.push('Bemerkung:');
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // Financial Summary - Two column format (label left, value right-aligned at column 48)
    // Thermal printer width: 48 characters
    const vorbestand = report.financial_summary.total_expenses.toFixed(2).replace('.', ',');
    const einnahmen = report.financial_summary.total_income.toFixed(2).replace('.', ',');
    const ausgaben = report.financial_summary.total_expenses.toFixed(2).replace('.', ',');
    const endbestand = report.financial_summary.final_balance.toFixed(2).replace('.', ',');
    
    const RECEIPT_WIDTH = 48; // 80mm thermal paper = 48 characters
    
    lines.push(LEFT);
    // Vorbestand - value ends at column 48 (right edge)
    const vorbestandLabel = 'Vorbestand:';
    const vorbestandValue = vorbestand + '€';
    const vorbestandSpaces = RECEIPT_WIDTH - vorbestandLabel.length - vorbestandValue.length;
    lines.push(BOLD_ON + vorbestandLabel + BOLD_OFF + ' '.repeat(vorbestandSpaces) + vorbestandValue);
    lines.push(FEED);
    
    // Einnahmen - value ends at column 48 (right edge)
    const einnahmenLabel = 'Einnahmen:';
    const einnahmenValue = einnahmen + '€';
    const einnahmenSpaces = RECEIPT_WIDTH - einnahmenLabel.length - einnahmenValue.length;
    lines.push(BOLD_ON + einnahmenLabel + BOLD_OFF + ' '.repeat(einnahmenSpaces) + einnahmenValue);
    lines.push(FEED);
    
    // Ausgaben - value ends at column 48 (right edge)
    const ausgabenLabel = 'Ausgaben:';
    const ausgabenValue = ausgaben + '€';
    const ausgabenSpaces = RECEIPT_WIDTH - ausgabenLabel.length - ausgabenValue.length;
    lines.push(BOLD_ON + ausgabenLabel + BOLD_OFF + ' '.repeat(ausgabenSpaces) + ausgabenValue);
    lines.push(FEED);
    
    // Endbestand - value ends at column 48 (right edge)
    const endbestandLabel = 'Endbestand:';
    const endbestandValue = endbestand + '€';
    const endbestandSpaces = RECEIPT_WIDTH - endbestandLabel.length - endbestandValue.length;
    lines.push(BOLD_ON + endbestandLabel + BOLD_OFF + ' '.repeat(endbestandSpaces) + endbestandValue);
    lines.push(FEED);

    // Separator line (matching PDF)
    lines.push('-'.repeat(48));
    lines.push(FEED);

    // EINNAHMEN Section - Table format matching PDF exactly
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('EINNAHMEN');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    
    // Table headers: Bez. | Steuer | Betrag (thermal printer width: 48 chars)
    lines.push(LEFT);
    lines.push(BOLD_ON);
    // Format: "Bez." (left, 18 chars), "Steuer" (center, 10 chars), "Betrag" (right, 20 chars)
    const bezHeader = 'Bez.';
    const steuerHeader = 'Steuer';
    const betragHeader = 'Betrag';
    lines.push(bezHeader.padEnd(18) + steuerHeader.padStart(10) + betragHeader.padStart(20));
    lines.push(BOLD_OFF);
    lines.push(FEED);
    
    // VAT 7.00% row - matching PDF format exactly
    lines.push(LEFT);
    const vat7Gross = report.vat_breakdown.vat_7_percent.gross.toFixed(2).replace('.', ',');
    const vat7Net = report.vat_breakdown.vat_7_percent.net.toFixed(2).replace('.', ',');
    const vat7Tax = report.vat_breakdown.vat_7_percent.tax.toFixed(2).replace('.', ',');
    const vat7Bez = '7,00%';
    const vat7Steuer = '7,00%';
    const vat7Betrag = vat7Gross + '€';
    // Table row: Bez (left 18) + Steuer (center 10) + Betrag (right 20)
    lines.push(vat7Bez.padEnd(18) + vat7Steuer.padStart(10) + vat7Betrag.padStart(20));
    lines.push(FEED);
    // N and S values on same line (left aligned) - matching PDF format exactly
    lines.push(LEFT);
    lines.push('N: ' + vat7Net + '€ S: ' + vat7Tax + '€');
    lines.push(FEED);
    
    // VAT 19.00% row
    lines.push(LEFT);
    const vat19Gross = report.vat_breakdown.vat_19_percent.gross.toFixed(2).replace('.', ',');
    const vat19Net = report.vat_breakdown.vat_19_percent.net.toFixed(2).replace('.', ',');
    const vat19Tax = report.vat_breakdown.vat_19_percent.tax.toFixed(2).replace('.', ',');
    const vat19Bez = '19,00%';
    const vat19Steuer = '19,00%';
    const vat19Betrag = vat19Gross + '€';
    // Table row: Bez (left 18) + Steuer (center 10) + Betrag (right 20)
    lines.push(vat19Bez.padEnd(18) + vat19Steuer.padStart(10) + vat19Betrag.padStart(20));
    lines.push(FEED);
    // N and S values on same line (left aligned) - matching PDF format exactly
    lines.push(LEFT);
    lines.push('N: ' + vat19Net + '€ S: ' + vat19Tax + '€');
    lines.push(FEED);
    
    // Totals (bold, two-column format matching PDF) - Right-aligned values at column 48
    const totalNet = report.vat_breakdown.total_net.toFixed(2).replace('.', ',');
    const totalGross = report.vat_breakdown.total_gross.toFixed(2).replace('.', ',');
    lines.push(LEFT);
    
    // Gesamt Netto - value ends at column 48 (right edge)
    const gesamtNettoLabel = 'Gesamt Netto:';
    const gesamtNettoValue = totalNet + '€';
    const gesamtNettoSpaces = RECEIPT_WIDTH - gesamtNettoLabel.length - gesamtNettoValue.length;
    lines.push(BOLD_ON + gesamtNettoLabel + BOLD_OFF + ' '.repeat(gesamtNettoSpaces) + gesamtNettoValue);
    lines.push(FEED);
    
    // Gesamt - value ends at column 48 (right edge)
    const gesamtLabel = 'Gesamt:';
    const gesamtValue = totalGross + '€';
    const gesamtSpaces = RECEIPT_WIDTH - gesamtLabel.length - gesamtValue.length;
    lines.push(BOLD_ON + gesamtLabel + BOLD_OFF + ' '.repeat(gesamtSpaces) + gesamtValue);
    lines.push(BOLD_OFF);
    lines.push(FEED);

    // Separator line (matching PDF)
    lines.push('-'.repeat(48));
    lines.push(FEED);

    // KASSENVORGÄNGE Section - Table format matching PDF
    if (report.cash_transactions.length > 0 || report.payment_methods.cash.amount > 0) {
      lines.push(LEFT);
      lines.push(BOLD_ON);
      lines.push('KASSENVORGÄNGE');
      lines.push(BOLD_OFF);
      lines.push(FEED);
      
      // Format matching PDF: Bezeichnung and Betrag (not Kategorie)
      lines.push(FEED);
      
      report.cash_transactions.forEach(transaction => {
        lines.push(LEFT);
        lines.push('Bezeichnung: ' + transaction.type);
        lines.push(FEED);
        lines.push('Betrag: ' + transaction.amount.toFixed(2).replace('.', ',') + '€');
        lines.push(FEED);
      });
      
      // If no transactions but cash payments exist, add bank deposit
      if (report.cash_transactions.length === 0 && report.payment_methods.cash.amount > 0) {
        lines.push(LEFT);
        lines.push('Bezeichnung: Bankeinzahlung');
        lines.push(FEED);
        lines.push('Betrag: ' + report.payment_methods.cash.amount.toFixed(2).replace('.', ',') + '€');
        lines.push(FEED);
      }
      // Separator line after KASSENVORGÄNGE (matching PDF)
      lines.push('-'.repeat(48));
      lines.push(FEED);
    }

    // ZAHLARTEN Section - Table format matching PDF
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('ZAHLARTEN');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    
    // Table headers: Zahlart | Betrag (48 chars total)
    lines.push(LEFT);
    lines.push(BOLD_ON);
    const zahlartHeader2 = 'Zahlart';
    const betragHeader2 = 'Betrag';
    lines.push(zahlartHeader2.padEnd(36) + betragHeader2.padStart(12));
    lines.push(BOLD_OFF);
    lines.push(FEED);
    
    if (report.payment_methods.cash.count > 0) {
      const cashAmount = report.payment_methods.cash.amount.toFixed(2).replace('.', ',');
      const label = `${report.payment_methods.cash.count} x Zahlart: bar`;
      lines.push(label.padEnd(35) + (cashAmount + '€').padStart(12));
    }
    if (report.payment_methods.takeaway_com.count > 0) {
      const takeawayAmount = report.payment_methods.takeaway_com.amount.toFixed(2).replace('.', ',');
      const label = `${report.payment_methods.takeaway_com.count} x Zahlart: Onlinezahlung takeaway.com`;
      // If label is longer than 35 chars, wrap to next line or truncate
      if (label.length > 35) {
        lines.push(label.substring(0, 35).padEnd(35) + (takeawayAmount + '€').padStart(12));
        lines.push(label.substring(35));
      } else {
        lines.push(label.padEnd(35) + (takeawayAmount + '€').padStart(12));
      }
    }
    if (report.payment_methods.ubereats.count > 0) {
      const ubereatsAmount = report.payment_methods.ubereats.amount.toFixed(2).replace('.', ',');
      const label = `${report.payment_methods.ubereats.count} x Zahlart: Onlinezahlung UberEats`;
      if (label.length > 35) {
        lines.push(label.substring(0, 35).padEnd(35) + (ubereatsAmount + '€').padStart(12));
        lines.push(label.substring(35));
      } else {
        lines.push(label.padEnd(35) + (ubereatsAmount + '€').padStart(12));
      }
    }
    lines.push(FEED);
    
    // Separator line after ZAHLARTEN (matching PDF)
    lines.push('-'.repeat(48));
    lines.push(FEED);

    // BAR ZAHLUNGEN NACH VERKAUFSKANAL Section - Matching PDF format
    if (report.sales_channels_cash.length > 0) {
      lines.push(LEFT);
      lines.push(BOLD_ON);
      lines.push('BAR ZAHLUNGEN NACH VERKAUFSKANAL');
      lines.push(BOLD_OFF);
      lines.push(FEED);
      lines.push(FEED);
      
      report.sales_channels_cash.forEach(channel => {
        lines.push(LEFT);
        lines.push('Bezeichnung: ' + channel.channel);
        lines.push(FEED);
        lines.push('Betrag: ' + channel.amount.toFixed(2).replace('.', ',') + '€');
        lines.push(FEED);
      });
      // Separator line after BAR ZAHLUNGEN NACH VERKAUFSKANAL (matching PDF)
      lines.push('-'.repeat(48));
      lines.push(FEED);
    }

    // ALLE ZAHLUNGEN NACH VERKAUFSKANAL Section - Matching PDF format
    if (report.sales_channels_all.length > 0) {
      lines.push(LEFT);
      lines.push(BOLD_ON);
      lines.push('ALLE ZAHLUNGEN NACH VERKAUFSKANAL');
      lines.push(BOLD_OFF);
      lines.push(FEED);
      lines.push(FEED);
      
      report.sales_channels_all.forEach(channel => {
        const channelCount = (channel as any).count || 1;
        lines.push(LEFT);
        lines.push(`${channelCount} x ${channel.channel} - ${channel.payment_method}`);
        lines.push(FEED);
        lines.push('Betrag: ' + channel.amount.toFixed(2).replace('.', ',') + '€');
        lines.push(FEED);
      });
      // Separator line after ALLE ZAHLUNGEN NACH VERKAUFSKANAL (matching PDF)
      lines.push('-'.repeat(48));
      lines.push(FEED);
    }

    // BONUSSYSTEM Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('BONUSSYSTEM');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push('Vergebene Punkte:');
    lines.push(FEED);
    const bonusAmount = report.bonus_system.amount.toFixed(2).replace('.', ',');
    lines.push(`Wert vergebener Punkte: ${bonusAmount}€`);
    lines.push(FEED);
    lines.push('Eingelöste Punkte:');
    lines.push(FEED);
    lines.push('Wert eingelöster Punkte: 0,00€');
    lines.push(FEED);
    lines.push(FEED);

    // Footer - KASSENABSCHLUSSBELEG (Large, Bold, Centered) - Matching PDF format
    lines.push(CENTER);
    lines.push(BOLD_ON);
    lines.push(SIZE_LARGE);
    lines.push('KASSENABSCHLUSSBELEG');
    lines.push(BOLD_OFF);
    lines.push(SIZE_NORMAL);
    lines.push(FEED);
    lines.push(FEED);

    // Cut paper
    lines.push(CUT);

    // CRITICAL: Join with empty string (not '\n') - ESC/POS commands must be concatenated directly
    // Each line already includes FEED (\x0A) where needed, and ESC/POS commands cannot have separators
    // This matches the format used in exactPrintFormats.ts for working receipts
    return lines.join('');
  };

  const generatePrintContent = (rawReport: DailyClosingReport, lang: 'en' | 'ar' = 'en'): string => {
    const report = normalizeReport(rawReport);
    const lines: string[] = [];

    // ESC/POS commands for professional thermal printing
    const ESC = '\x1B';
    const CENTER = ESC + '\x61\x01';  // Center align
    const LEFT = ESC + '\x61\x00';    // Left align
    const BOLD_ON = ESC + '\x45\x01'; // Bold on
    const BOLD_OFF = ESC + '\x45\x00'; // Bold off
    const SIZE_NORMAL = ESC + '\x21\x00'; // Normal size
    const SIZE_LARGE = ESC + '\x21\x30'; // Large size (2x width & height)
    const FEED = '\x0A'; // Line feed
    const CUT = '\x1D\x56\x00'; // Cut paper
    const INIT = '\x1B\x40'; // Initialize printer

    // Initialize printer
    lines.push(INIT);
    lines.push(FEED);

    // Set code page to support German characters (Code Page 858 for European characters)
    // Code Page 858 = Windows-1252 extended with Euro symbol
    const CODE_PAGE_858 = ESC + '\x74\x13'; // ESC t 19 (Code Page 858)
    lines.push(CODE_PAGE_858);

    // LOGO AREA (will be replaced by print server)
    // The print server will inject the bitmap logo here
    lines.push(CENTER);
    lines.push('[LOGO_PLACEHOLDER]');
    lines.push(FEED);
    console.log('🔍 [DEBUG] Logo placeholder added to daily closing report');

    // Restaurant Name (Large, Bold) - Professional Header
    // This text appears AFTER logo placeholder - if logo is bitmap, this shows below it
    // If logo is text fallback (empty), this shows in place of logo
    lines.push(CENTER);
    lines.push(BOLD_ON);
    lines.push(SIZE_LARGE);
    lines.push('SAHEEH');
    lines.push(BOLD_OFF);
    lines.push(SIZE_NORMAL);
    lines.push('SUSHI & BOWLS');
    lines.push(FEED);

    // Business Information - EXACT MATCH (with German characters)
    // Use actual German characters - they will be properly encoded when sent to printer
    // The printer will interpret them according to Code Page 858
    lines.push(CENTER);
    lines.push('Saheeh Hürth');
    lines.push('Sudetenstraße 60');
    lines.push('50354 Hürth');
    lines.push('022337063324');
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // Report Title - Large and Bold
    lines.push(CENTER);
    lines.push(BOLD_ON);
    lines.push(SIZE_LARGE);
    if (lang === 'ar') {
      lines.push('TAGESABSCHLUSSBERICHT');
      lines.push(BOLD_OFF);
      lines.push(SIZE_NORMAL);
      lines.push('(Daily Closing Report)');
    } else {
      lines.push('DAILY CLOSING REPORT');
      lines.push(BOLD_OFF);
      lines.push(SIZE_NORMAL);
      lines.push('(Tagesabschlussbericht)');
    }
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // Report Details
    const dateLocale = lang === 'ar' ? 'ar-SA' : 'en-US';
    const timeLocale = lang === 'ar' ? 'ar-SA' : 'en-US';
    lines.push(LEFT);
    lines.push(`${lang === 'ar' ? 'Datum' : 'Date'}: ${new Date(report.report_date).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}`);
    lines.push(`${lang === 'ar' ? 'Kassierer' : 'Cashier'}: ${report.cashier_name}`);
    lines.push(`${lang === 'ar' ? 'Filiale' : 'Branch'}: ${report.branch}`);
    const shiftStart = report.shift_start ? new Date(report.shift_start).toLocaleTimeString(timeLocale, { hour: '2-digit', minute: '2-digit' }) : '09:00';
    const shiftEnd = report.shift_end ? new Date(report.shift_end).toLocaleTimeString(timeLocale, { hour: '2-digit', minute: '2-digit' }) : '23:59';
    lines.push(`${lang === 'ar' ? 'Schicht' : 'Shift'}: ${shiftStart} - ${shiftEnd}`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // FINANZÜBERSICHT Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('FINANZÜBERSICHT:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push(`Gesamtumsatz: ${report.financial_summary.total_sales.toFixed(2)} EUR`);
    lines.push(`Anzahl Bestellungen: ${report.financial_summary.total_orders}`);
    lines.push(`Anzahl Kunden: ${report.financial_summary.total_customers}`);
    lines.push(`Durchschnittlicher Bestellwert: ${report.financial_summary.average_order_value.toFixed(2)} EUR`);
    lines.push(`Bruttoumsatz: ${report.financial_summary.gross_revenue.toFixed(2)} EUR`);
    lines.push(`Nettoumsatz: ${report.financial_summary.net_revenue.toFixed(2)} EUR`);
    lines.push(`MwSt. (19%): ${report.financial_summary.tax_collected.toFixed(2)} EUR`);
    lines.push(`Gewährte Rabatte: ${report.financial_summary.discounts_given.toFixed(2)} EUR`);
    lines.push(`Erstattungen: ${report.financial_summary.refunds_issued.toFixed(2)} EUR`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // GETEILTE ZAHLUNGEN Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('GETEILTE ZAHLUNGEN:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push(`Anzahl geteilter Bestellungen: ${report.financial_summary.split_payment_orders || 0}`);
    lines.push(`Umsatz aus geteilten Zahlungen: ${(report.financial_summary.split_payment_revenue || 0).toFixed(2)} EUR`);
    lines.push(`Anteil geteilter Zahlungen: ${(report.financial_summary.split_payment_percentage || 0).toFixed(2)}%`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // ZAHLUNGSMETHODEN Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('ZAHLUNGSMETHODEN:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push(`Bargeld: ${report.payment_methods.cash.amount.toFixed(2)} EUR (${report.payment_methods.cash.percentage.toFixed(1)}%)`);
    lines.push(`Karte: ${report.payment_methods.card.amount.toFixed(2)} EUR (${report.payment_methods.card.percentage.toFixed(1)}%)`);
    lines.push(`PayPal: ${report.payment_methods.paypal.amount.toFixed(2)} EUR (${report.payment_methods.paypal.percentage.toFixed(1)}%)`);
    lines.push(`Sonstige: ${report.payment_methods.other.amount.toFixed(2)} EUR (${report.payment_methods.other.percentage.toFixed(1)}%)`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // KASSENABRECHNUNG Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('KASSENABRECHNUNG:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push(`Kassenbestand Anfang: ${report.cash_reconciliation.opening_cash.toFixed(2)} EUR`);
    lines.push(`Bargeldumsatz: ${report.cash_reconciliation.cash_sales.toFixed(2)} EUR`);
    lines.push(`Bargelderstattungen: ${report.cash_reconciliation.cash_refunds.toFixed(2)} EUR`);
    lines.push(`Erwarteter Kassenbestand: ${report.cash_reconciliation.expected_cash.toFixed(2)} EUR`);
    lines.push(`Tatsächlicher Kassenbestand: ${report.cash_reconciliation.actual_cash.toFixed(2)} EUR`);

    // Highlight cash difference (bold if not zero)
    const cashDiff = report.cash_reconciliation.cash_difference;
    if (Math.abs(cashDiff) > 0.01) {
      lines.push(BOLD_ON);
      lines.push(`Kassendifferenz: ${cashDiff.toFixed(2)} EUR`);
      lines.push(BOLD_OFF);
    } else {
      lines.push(`Kassendifferenz: ${cashDiff.toFixed(2)} EUR`);
    }

    lines.push(`Kassenüberschuss/-fehlbetrag: ${report.cash_reconciliation.cash_over_short.toFixed(2)} EUR`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // TOP-VERKAUFSARTIKEL Section
    if ((report.sales_analysis?.top_selling_items || []).length > 0) {
      lines.push(LEFT);
      lines.push(BOLD_ON);
      lines.push('TOP-VERKAUFSARTIKEL:');
      lines.push(BOLD_OFF);
      lines.push(FEED);
      (report.sales_analysis?.top_selling_items || []).slice(0, 5).forEach((item, index) => {
        lines.push(`${index + 1}. ${item.name}: ${item.quantity}x - ${item.revenue.toFixed(2)} EUR`);
      });
      lines.push(FEED);
      lines.push('--------------------------------');
      lines.push(FEED);
    }

    // BETRIEBSKENNZAHLEN Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('BETRIEBSKENNZAHLEN:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    if ((report.operational_metrics?.peak_hours || []).length > 0) {
      lines.push(`Hauptgeschäftszeiten: ${report.operational_metrics.peak_hours.join(', ')}`);
    }
    if ((report.operational_metrics?.slow_hours || []).length > 0) {
      lines.push(`Schwache Zeiten: ${report.operational_metrics.slow_hours.join(', ')}`);
    }
    lines.push(`Durchschnittliche Servicezeit: ${report.operational_metrics?.average_service_time ?? 0} Minuten`);
    lines.push(`Kundenzufriedenheit: ${report.operational_metrics?.customer_satisfaction_score ?? 0}/5`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // PRÜFSPUR Section
    lines.push(LEFT);
    lines.push(BOLD_ON);
    lines.push('PRÜFSPUR:');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push(`Bericht erstellt: ${new Date(report.audit_trail?.report_generated_at || report.created_at || Date.now()).toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`);
    lines.push(`Erstellt von: ${report.audit_trail?.generated_by || report.cashier_name || 'System'}`);
    if (report.audit_trail.first_order_time) {
      lines.push(`Erste Bestellung: ${new Date(report.audit_trail.first_order_time).toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`);
    }
    if (report.audit_trail.last_order_time) {
      lines.push(`Letzte Bestellung: ${new Date(report.audit_trail.last_order_time).toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`);
    }
    lines.push(`Systemlaufzeit: ${Math.floor(report.audit_trail.system_uptime / 3600)} Stunden`);
    lines.push(`Protokollierte Fehler: ${report.audit_trail.errors_logged}`);
    lines.push(FEED);
    lines.push('--------------------------------');
    lines.push(FEED);

    // Footer
    lines.push(CENTER);
    lines.push(BOLD_ON);
    lines.push('ENDE DES TAGESABSCHLUSSBERICHTS');
    lines.push(BOLD_OFF);
    lines.push(FEED);
    lines.push('SAHEEH SUSHI & BOWLS');
    lines.push(FEED);
    lines.push(FEED);
    lines.push(FEED);

    // Cut paper
    lines.push(CUT);

    return lines.join('\n');
  };

  const exportReportToPDF = async (rawReport: DailyClosingReport) => {
    try {
      const report = normalizeReport(rawReport);
      console.log('📄 [EXPORT] Exporting daily closing report to PDF...');

      // Import jsPDF
      const { jsPDF } = await import('jspdf');
      const { autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF();

      // Load PNG logo - prioritize local file (no CORS), fallback to cache service
      console.log('🖼️ [PDF] Loading PNG logo...');

      let logoData: string | null = null;
      let logoAdded = false;

      // Method 1: Try loading from local public folder first (same origin, no CORS)
      try {
        const localLogoUrl = '/saheeh_logo_w.svg';
        console.log('🖼️ [PDF] Attempting to load logo from local file:', localLogoUrl);

        const localResponse = await fetch(localLogoUrl, { cache: 'no-cache' });

        if (localResponse.ok) {
          const blob = await localResponse.blob();
          console.log('📦 [PDF] Logo blob received:', { size: blob.size, type: blob.type });

          if (blob.size === 0) {
            throw new Error('Logo blob is empty');
          }

          logoData = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result && typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
                console.log('✅ [PDF] Logo loaded from local file (no CORS issues), length:', reader.result.length);
                resolve(reader.result);
              } else {
                reject(new Error('Invalid logo data from local file'));
              }
            };
            reader.onerror = () => reject(new Error('FileReader error'));
            reader.readAsDataURL(blob);
          });
        } else {
          throw new Error(`Local logo not found: ${localResponse.status} ${localResponse.statusText}`);
        }
      } catch (localError) {
        console.warn('⚠️ [PDF] Local logo not available, trying LogoCacheService:', localError);

        // Method 2: Use LogoCacheService to load from Firebase Storage (handles CORS via Image element)
        try {
          logoData = await LogoCacheService.getLogo();
          if (logoData && logoData.length > 100) {
            console.log('✅ [PDF] Logo loaded from LogoCacheService, length:', logoData.length);
          } else {
            throw new Error('LogoCacheService returned invalid data');
          }
        } catch (cacheError) {
          console.error('❌ [PDF] Failed to load logo from LogoCacheService:', cacheError);
          logoData = null;
        }
      }

      // Add app logo - CENTERED at top of A4 page (210mm wide)
      // Logo: 50mm wide, centered → x = (210 - 50) / 2 = 80
      if (logoData && logoData.length > 100 && logoData.startsWith('data:image/')) {
        try {
          let actualFormat: 'PNG' | 'JPEG' = 'PNG';
          if (logoData.startsWith('data:image/jpeg') || logoData.startsWith('data:image/jpg')) {
            actualFormat = 'JPEG';
          }

          // Logo actual pixels: 4520x3508 → ratio 1.288:1
          const logoWidth = 40;
          const logoHeight = logoWidth / 1.288; // ~31mm, correct aspect ratio
          const logoX = (210 - logoWidth) / 2; // Center on A4 page
          doc.addImage(logoData, actualFormat, logoX, 8, logoWidth, logoHeight);
          logoAdded = true;
          console.log('✅ [PDF] Logo added to PDF successfully');
        } catch (imgError) {
          console.error('❌ [PDF] Failed to add logo image to PDF:', imgError);
          logoAdded = false;
        }
      }

      // Fallback: If logo failed, use styled text
      if (!logoAdded) {
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('SAHEEH', 105, 22, { align: 'center' });
      }

      // Business info below logo (logo is ~31mm tall when added, starts at y=8)
      const infoY = logoAdded ? 43 : 30;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text('Äußere Kanalstraße 211 · 50827 Köln · Steuernummer: 217/5786/1785', 105, infoY, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      // Decorative line
      const lineY = infoY + 4;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(20, lineY, 190, lineY);

      // Report title
      const titleY = lineY + 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('TAGESABSCHLUSSBERICHT', 105, titleY, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Daily Closing Report', 105, titleY + 5, { align: 'center' });
      doc.setTextColor(0, 0, 0);

      // Report details — compact row
      const detailsY = titleY + 14;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Datum: ${new Date(report.report_date).toLocaleDateString('en-US')}`, 20, detailsY);
      doc.text(`Kassierer: ${report.cashier_name}`, 85, detailsY);
      doc.text(`Filiale: ${report.branch}`, 155, detailsY);

      // Financial Summary Table
      const financialData = [
        ['Gesamtumsatz', `${report.financial_summary.total_sales.toFixed(2)} EUR`],
        ['Anzahl Bestellungen', report.financial_summary.total_orders.toString()],
        ['Anzahl Kunden', report.financial_summary.total_customers.toString()],
        ['Durchschnittlicher Bestellwert', `${report.financial_summary.average_order_value.toFixed(2)} EUR`],
        ['Bruttoumsatz', `${report.financial_summary.gross_revenue.toFixed(2)} EUR`],
        ['Nettoumsatz', `${report.financial_summary.net_revenue.toFixed(2)} EUR`],
        ['MwSt. (19%)', `${report.financial_summary.tax_collected.toFixed(2)} EUR`],
        ['Gewährte Rabatte', `${report.financial_summary.discounts_given.toFixed(2)} EUR`],
        ['Erstattungen', `${report.financial_summary.refunds_issued.toFixed(2)} EUR`]
      ];

      // Professional table styling
      const tableHeadStyle = { fillColor: [26, 32, 44] as [number, number, number], textColor: 255, fontSize: 9, fontStyle: 'bold' as const, cellPadding: 3 };
      const tableBodyStyle = { fontSize: 9, cellPadding: 2.5 };
      const tableAlternateRow = { fillColor: [247, 250, 252] as [number, number, number] };

      const tableStartY = detailsY + 10;
      autoTable(doc, {
        startY: tableStartY,
        head: [['FINANZÜBERSICHT', '']],
        body: financialData,
        theme: 'striped',
        headStyles: tableHeadStyle,
        styles: tableBodyStyle,
        alternateRowStyles: tableAlternateRow,
        margin: { left: 20, right: 20 },
      });

      // Payment Methods Table
      const paymentData = [
        ['Bargeld', `${report.payment_methods.cash.amount.toFixed(2)} EUR`, `${report.payment_methods.cash.percentage.toFixed(1)}%`],
        ['Karte', `${report.payment_methods.card.amount.toFixed(2)} EUR`, `${report.payment_methods.card.percentage.toFixed(1)}%`],
        ['PayPal', `${report.payment_methods.paypal.amount.toFixed(2)} EUR`, `${report.payment_methods.paypal.percentage.toFixed(1)}%`],
        ['Sonstige', `${report.payment_methods.other.amount.toFixed(2)} EUR`, `${report.payment_methods.other.percentage.toFixed(1)}%`]
      ];

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 12,
        head: [['ZAHLUNGSMETHODEN', 'Betrag', 'Prozent']],
        body: paymentData,
        theme: 'striped',
        headStyles: tableHeadStyle,
        styles: tableBodyStyle,
        alternateRowStyles: tableAlternateRow,
        margin: { left: 20, right: 20 },
      });

      // Cash Reconciliation Table
      const cashData = [
        ['Kassenbestand Anfang', `${report.cash_reconciliation.opening_cash.toFixed(2)} EUR`],
        ['Bargeldumsatz', `${report.cash_reconciliation.cash_sales.toFixed(2)} EUR`],
        ['Bargelderstattungen', `${report.cash_reconciliation.cash_refunds.toFixed(2)} EUR`],
        ['Erwarteter Kassenbestand', `${report.cash_reconciliation.expected_cash.toFixed(2)} EUR`],
        ['Tatsächlicher Kassenbestand', `${report.cash_reconciliation.actual_cash.toFixed(2)} EUR`],
        ['Kassendifferenz', `${report.cash_reconciliation.cash_difference.toFixed(2)} EUR`],
        ['Kassenüberschuss/-fehlbetrag', `${report.cash_reconciliation.cash_over_short.toFixed(2)} EUR`]
      ];

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 12,
        head: [['KASSENABRECHNUNG', '']],
        body: cashData,
        theme: 'striped',
        headStyles: tableHeadStyle,
        styles: tableBodyStyle,
        alternateRowStyles: tableAlternateRow,
        margin: { left: 20, right: 20 },
      });

      // Top Selling Items Table (skip if no data)
      const topItems = (report.sales_analysis?.top_selling_items || []).slice(0, 5);
      if (topItems.length > 0) {
        const topItemsData = topItems.map((item, index) => [
          `${index + 1}. ${item.name}`,
          item.quantity.toString(),
          `${(item.revenue || 0).toFixed(2)} EUR`
        ]);

        autoTable(doc, {
          startY: (doc as any).lastAutoTable.finalY + 12,
          head: [['TOP-VERKAUFSARTIKEL', 'Anzahl', 'Umsatz']],
          body: topItemsData,
          theme: 'striped',
          headStyles: tableHeadStyle,
          styles: tableBodyStyle,
          alternateRowStyles: tableAlternateRow,
          margin: { left: 20, right: 20 },
        });
      }

      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(20, pageHeight - 25, 190, pageHeight - 25);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Erstellt: ${new Date(report.audit_trail?.report_generated_at || report.created_at || Date.now()).toLocaleString('en-US')} · Von: ${report.audit_trail?.generated_by || report.cashier_name || 'System'}`, 20, pageHeight - 20);
      doc.text('SAHEEH · Tagesabschlussbericht', 105, pageHeight - 15, { align: 'center' });

      // Save PDF
      const fileName = `Tagesabschlussbericht_${report.report_date}_${report.cashier_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);

      console.log('✅ [EXPORT] PDF exported successfully');

    } catch (error) {
      console.error('❌ [EXPORT] Failed to export PDF:', error);
      alert(t('daily_closing.export_error'));
    }
  };


  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#68d391' }} />
        <Typography variant="body2" sx={{ mt: 2, color: '#9ca3af' }}>
          {t('daily_closing.title') || 'Loading Daily Closing Reports...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: 'auto', sm: '100vh' },
        minHeight: { xs: 'calc(100vh - 56px)' },
        backgroundColor: '#1e2139',
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'auto', sm: 'hidden' },
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Premium Header */}
      <Box
        sx={{
          px: { xs: 1.5, md: 3 }, py: { xs: 1.25, md: 2.5 },
          background: { xs: '#252838', md: 'linear-gradient(180deg, #1e2030 0%, #252838 100%)' },
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          position: { xs: 'sticky', sm: 'relative' },
          top: { xs: 0 },
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'center', md: 'flex-end' }, gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 2 } }}>
            <Box sx={{
              width: { xs: 42, md: 54 }, height: { xs: 42, md: 54 }, borderRadius: '14px',
              background: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(0, 200, 150,0.28)',
              flexShrink: 0,
            }}>
              <ReceiptLong sx={{ fontSize: { xs: 22, md: 28 }, color: '#0f1117' }} />
            </Box>
            <Box>
              <Typography sx={{
                fontWeight: 800, color: '#fff',
                fontSize: { xs: '1rem', md: '1.55rem' },
                letterSpacing: '-0.4px', lineHeight: 1.1,
              }}>
                {t('daily_closing.title')}
              </Typography>
              <Typography sx={{
                color: '#8a93b0', fontSize: { xs: '0.7rem', md: '0.82rem' },
                fontWeight: 500, mt: 0.25,
              }}>
                {t('daily_closing.subtitle')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Assessment sx={{ fontSize: 18 }} />}
              onClick={() => setGenerateReportDialog(true)}
              sx={{
                textTransform: 'none', fontWeight: 700,
                fontSize: { xs: '0.72rem', md: '0.82rem' },
                borderRadius: '10px', px: { xs: 1.75, md: 2.25 }, height: 40,
                background: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
                color: '#0f1117',
                boxShadow: '0 4px 14px rgba(0, 200, 150,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #a3ef2e 0%, #6fb017 100%)',
                  boxShadow: '0 6px 18px rgba(0, 200, 150,0.4)',
                },
              }}
            >
              {t('daily_closing.generate_report')}
            </Button>
            <IconButton
              onClick={loadData}
              sx={{
                color: '#9ca3af',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                width: 40, height: 40,
                backgroundColor: 'rgba(255,255,255,0.02)',
                display: { xs: 'flex', md: 'none' },
                '&:hover': { borderColor: '#00C896', color: '#00C896' },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              onClick={loadData}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                borderColor: 'rgba(255,255,255,0.08)', color: '#b4bccc',
                borderRadius: '10px', height: 40, px: 2,
                backgroundColor: 'rgba(255,255,255,0.02)',
                display: { xs: 'none', md: 'inline-flex' },
                '&:hover': { borderColor: '#00C896', color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.06)' },
              }}
            >
              {t('daily_closing.refresh')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Logout sx={{ fontSize: 18 }} />}
              onClick={() => navigate('/dashboard')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
                borderColor: 'rgba(255,255,255,0.08)', color: '#b4bccc',
                borderRadius: '10px', height: 40, px: 2,
                backgroundColor: 'rgba(255,255,255,0.02)',
                display: { xs: 'none', md: 'inline-flex' },
                '&:hover': { borderColor: '#00C896', color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.06)' },
              }}
            >
              {t('daily_closing.back_dashboard')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, maxWidth: 1400, mx: 'auto', flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Premium Action Cards */}
        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
          {/* Card 1: Interim Summary */}
          <Grid item xs={12} md={4}>
            <Box sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              height: '100%',
              background: 'linear-gradient(145deg, #1e2139 0%, #252838 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: { md: 'translateY(-3px)' },
                borderColor: 'rgba(0, 200, 150,0.4)',
                boxShadow: '0 14px 40px -12px rgba(0, 200, 150,0.35)',
              },
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
              },
              '&::after': {
                content: '""', position: 'absolute',
                top: -50, right: -50, width: 160, height: 160,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(0, 200, 150,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              },
            }}>
              <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '11px',
                    background: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(0, 200, 150,0.3)',
                  }}>
                    <TrendingUp sx={{ fontSize: 22, color: '#0f1117' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem', lineHeight: 1.15, letterSpacing: '-0.3px' }}>
                      {t('daily_closing.interim_summary')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '0.6px', mt: 0.25 }}>
                      Live
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: '#8a93b0', fontSize: '0.82rem', mb: 2.5, flexGrow: 1, lineHeight: 1.5 }}>
                  {t('daily_closing.interim_description')}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Assessment sx={{ fontSize: 18 }} />}
                  onClick={generateInterimSummary}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
                    color: '#0f1117', fontSize: '0.88rem', py: 1.25,
                    boxShadow: '0 6px 16px rgba(0, 200, 150,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #a3ef2e 0%, #6fb017 100%)',
                      boxShadow: '0 8px 22px rgba(0, 200, 150,0.45)',
                    },
                  }}
                >
                  {t('daily_closing.generate_interim')}
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Card 2: Zwischenbericht */}
          <Grid item xs={12} md={4}>
            <Box sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              height: '100%',
              background: 'linear-gradient(145deg, #1e2139 0%, #252838 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: { md: 'translateY(-3px)' },
                borderColor: 'rgba(251,146,60,0.4)',
                boxShadow: '0 14px 40px -12px rgba(251,146,60,0.35)',
              },
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)',
              },
              '&::after': {
                content: '""', position: 'absolute',
                top: -50, right: -50, width: 160, height: 160,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(251,146,60,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              },
            }}>
              <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '11px',
                    background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(251,146,60,0.3)',
                  }}>
                    <ReceiptLong sx={{ fontSize: 22, color: '#0f1117' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem', lineHeight: 1.15, letterSpacing: '-0.3px' }}>
                      Zwischenbericht
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fb923c', textTransform: 'uppercase', letterSpacing: '0.6px', mt: 0.25 }}>
                      German Format
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: '#8a93b0', fontSize: '0.82rem', mb: 2, flexGrow: 1, lineHeight: 1.5 }}>
                  {t('daily_closing.interim_format_description')}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => generateZwischenbericht(true)}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)',
                    color: '#0f1117', fontSize: '0.88rem', py: 1.25, mb: 0.75,
                    boxShadow: '0 6px 16px rgba(251,146,60,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #fdaa66 0%, #f97316 100%)',
                      boxShadow: '0 8px 22px rgba(251,146,60,0.45)',
                    },
                  }}
                >
                  {t('daily_closing.generate_preview')}
                </Button>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    startIcon={<LocalPrintshop sx={{ fontSize: 15 }} />}
                    onClick={async () => {
                      if (generatedZwischenbericht) {
                        await printZwischenbericht(generatedZwischenbericht);
                      } else {
                        await generateZwischenbericht(false);
                      }
                    }}
                    disabled={!generatedZwischenbericht}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '9px',
                      fontSize: '0.76rem', py: 0.85,
                      borderColor: 'rgba(255,255,255,0.08)', color: '#b4bccc',
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      '&:hover': { borderColor: '#fb923c', color: '#fb923c', backgroundColor: 'rgba(251,146,60,0.06)' },
                      '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.04)', color: '#4a4d62' },
                    }}
                  >
                    {t('orders.print')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    startIcon={<PictureAsPdf sx={{ fontSize: 15 }} />}
                    onClick={async () => {
                      if (generatedZwischenbericht) {
                        await exportZwischenberichtToPDF(generatedZwischenbericht);
                      } else {
                        const zb = await reportsService.generateZwischenbericht(
                          reportForm.reportDate || new Date().toISOString().split('T')[0],
                          reportForm.cashierName || currentUserInfo?.name || 'Staff Member',
                          'Kasse 1'
                        );
                        setGeneratedZwischenbericht(zb);
                        await exportZwischenberichtToPDF(zb);
                      }
                    }}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '9px',
                      fontSize: '0.76rem', py: 0.85,
                      borderColor: 'rgba(255,255,255,0.08)', color: '#b4bccc',
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      '&:hover': { borderColor: '#ef4444', color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.06)' },
                    }}
                  >
                    PDF
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Card 3: Print Reports */}
          <Grid item xs={12} md={4}>
            <Box sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              height: '100%',
              background: 'linear-gradient(145deg, #1e2139 0%, #252838 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: { md: 'translateY(-3px)' },
                borderColor: 'rgba(96,165,250,0.4)',
                boxShadow: '0 14px 40px -12px rgba(96,165,250,0.35)',
              },
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)',
              },
              '&::after': {
                content: '""', position: 'absolute',
                top: -50, right: -50, width: 160, height: 160,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(96,165,250,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              },
            }}>
              <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '11px',
                    background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(96,165,250,0.3)',
                  }}>
                    <LocalPrintshop sx={{ fontSize: 22, color: '#0f1117' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem', lineHeight: 1.15, letterSpacing: '-0.3px' }}>
                      {t('daily_closing.print_reports')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.6px', mt: 0.25 }}>
                      ESC / POS
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: '#8a93b0', fontSize: '0.82rem', mb: 2.5, flexGrow: 1, lineHeight: 1.5 }}>
                  {t('daily_closing.print_direct_description')}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<LocalPrintshop sx={{ fontSize: 18 }} />}
                  onClick={async () => {
                    try {
                      const today = new Date().toISOString().split('T')[0];
                      const cashierName = currentUserInfo?.name || currentUserInfo?.email || 'Staff Member';
                      const zwischenbericht = await reportsService.generateZwischenbericht(
                        today,
                        cashierName,
                        'Kasse 1'
                      );
                      await printZwischenbericht(zwischenbericht);
                    } catch (error: any) {
                      console.error('❌ [PRINT REPORTS] Failed to print Zwischenbericht:', error);
                      alert(t('daily_closing.print_interim_error') + ': ' + (error.message || t('payments.unknown')));
                    }
                  }}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)',
                    color: '#0f1117', fontSize: '0.88rem', py: 1.25,
                    boxShadow: '0 6px 16px rgba(96,165,250,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #79b3fb 0%, #3b82f6 100%)',
                      boxShadow: '0 8px 22px rgba(96,165,250,0.45)',
                    },
                  }}
                >
                  {t('daily_closing.print_latest')}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Daily Reports History */}
        <Box sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 'none', md: 'calc(100vh - 400px)' },
          background: 'linear-gradient(180deg, #1a1d29 0%, #1e2139 100%)',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          {/* Section Header */}
          <Box sx={{
            px: { xs: 1.75, md: 3 }, py: 1.75,
            background: 'linear-gradient(180deg, #1e2030 0%, #1a1d29 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0, gap: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '9px',
                background: 'rgba(0, 200, 150,0.1)',
                border: '1px solid rgba(0, 200, 150,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: '#00C896' }} />
              </Box>
              <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '0.95rem', md: '1.05rem' }, letterSpacing: '-0.2px' }}>
                {t('daily_closing.history')}
              </Typography>
            </Box>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              px: 1.25, py: 0.5, borderRadius: '8px',
              background: 'rgba(0, 200, 150,0.1)',
              border: '1px solid rgba(0, 200, 150,0.25)',
            }}>
              <Typography sx={{ color: '#00C896', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.3px' }}>
                {dailyReports.length} {t('daily_closing.reports_count')}
              </Typography>
            </Box>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#a0aec0' }} />
              </Box>
            ) : dailyReports.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: '#a0aec0' }}>{t('daily_closing.no_reports_found')}</Typography>
              </Box>
            ) : dailyReports.map((report, index) => {
              const isAutoGenerated = (report as any).auto_generated === true;
              const fs = report.financial_summary || { total_sales: 0, total_orders: 0 } as any;
              return (
                <Box key={report.id} sx={{
                  px: 1.5, py: 1.25, borderBottom: '1px solid #2a2d3e',
                  backgroundColor: index % 2 === 0 ? '#252838' : '#1e2139',
                  borderLeft: isAutoGenerated ? '3px solid #ed8936' : 'none',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '0.9rem' }}>
                        {new Date(report.report_date).toLocaleDateString('en-US')}
                      </Typography>
                      <Box sx={{
                        px: 0.75, py: 0.15, borderRadius: '4px',
                        backgroundColor: report.status === 'final' ? 'rgba(76,175,80,0.12)' : 'rgba(255,193,7,0.12)',
                      }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: report.status === 'final' ? '#38a169' : '#b7791f', textTransform: 'uppercase' }}>
                          {report.status}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '1rem' }}>
                      €{(fs.total_sales || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.72rem' }}>
                      {fs.total_orders || 0} orders · {report.cashier_name || 'System'}
                      {isAutoGenerated && <span style={{ color: '#ed8936', fontWeight: 600 }}> · AUTO</span>}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => { setSelectedReport(normalizeReport(report)); setViewReportDialog(true); }}
                        sx={{ width: 28, height: 28, color: '#63b3ed' }}><Assessment sx={{ fontSize: 14 }} /></IconButton>
                      <IconButton size="small" onClick={() => handlePrintPreview(report)}
                        sx={{ width: 28, height: 28, color: '#9ca3af' }}><Print sx={{ fontSize: 14 }} /></IconButton>
                      <IconButton size="small" onClick={() => exportReportToPDF(normalizeReport(report))}
                        sx={{ width: 28, height: 28, color: '#e53e3e' }}><FileDownload sx={{ fontSize: 14 }} /></IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Desktop Column Headers */}
          <Box sx={{
            px: 3, py: 1.5,
            backgroundColor: '#0f1117',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
          }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item sm={1.5}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px' }}>
                  {t('daily_closing.date')}
                </Typography>
              </Grid>
              <Grid item sm={2}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px' }}>
                  {t('daily_closing.cashier')}
                </Typography>
              </Grid>
              <Grid item sm={2}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px' }}>
                  {t('daily_closing.total_sales')}
                </Typography>
              </Grid>
              <Grid item sm={1}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px', textAlign: 'center', display: 'block' }}>
                  {t('daily_closing.orders')}
                </Typography>
              </Grid>
              <Grid item sm={1.5}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px' }}>
                  {t('daily_closing.cash_difference')}
                </Typography>
              </Grid>
              <Grid item sm={1}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px', textAlign: 'center', display: 'block' }}>
                  {t('daily_closing.status')}
                </Typography>
              </Grid>
              <Grid item sm={3}>
                <Typography sx={{ fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.6px', textAlign: 'right', display: 'block' }}>
                  {t('daily_closing.actions')}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Report Rows - Desktop only */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            display: { xs: 'none', md: 'block' },
            '&::-webkit-scrollbar': { width: '10px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: '10px', '&:hover': { background: 'rgba(0, 200, 150,0.3)' } },
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: '#00C896' }} />
              </Box>
            ) : dailyReports.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 72, height: 72, borderRadius: '50%', mb: 2,
                  background: 'linear-gradient(135deg, rgba(0, 200, 150,0.08) 0%, rgba(59,130,246,0.08) 100%)',
                  border: '1px solid rgba(0, 200, 150,0.2)',
                }}>
                  <CalendarMonth sx={{ fontSize: 32, color: '#00C896' }} />
                </Box>
                <Typography sx={{ color: '#e8eaf6', fontSize: '1rem', fontWeight: 700 }}>
                  {t('daily_closing.no_reports_found')}
                </Typography>
              </Box>
            ) : (
              dailyReports.map((report, index) => {
                const isAutoGenerated = (report as any).auto_generated === true;
                const fs = report.financial_summary || { total_sales: 0, total_orders: 0 } as any;
                const cr = report.cash_reconciliation || { cash_difference: 0 } as any;
                const diff = cr.cash_difference || 0;
                const diffAccent = diff === 0 ? '#00C896' : (Math.abs(diff) < 1 ? '#f59e0b' : '#ef4444');
                const statusIsFinal = report.status === 'final';
                return (
                  <Box
                    key={report.id}
                    sx={{
                      position: 'relative',
                      px: 3, py: 1.75,
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 200, 150,0.04)',
                        '& .row-accent': { opacity: 1, width: '4px' },
                      },
                    }}
                  >
                    <Box className="row-accent" sx={{
                      position: 'absolute', left: 0, top: 6, bottom: 6,
                      width: isAutoGenerated ? '3px' : '2px',
                      background: isAutoGenerated
                        ? 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)'
                        : 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
                      opacity: isAutoGenerated ? 0.8 : 0.4,
                      transition: 'all 0.18s ease',
                      borderRadius: '0 3px 3px 0',
                    }} />
                    <Grid container spacing={1} alignItems="center">
                      {/* Date */}
                      <Grid item sm={1.5}>
                        <Typography sx={{
                          fontWeight: 700, color: '#e8eaf6', fontSize: '0.88rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          letterSpacing: '-0.2px',
                        }}>
                          {new Date(report.report_date).toLocaleDateString('en-US')}
                        </Typography>
                      </Grid>

                      {/* Cashier */}
                      <Grid item sm={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '8px',
                            background: isAutoGenerated
                              ? 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#0f1117', fontWeight: 800, fontSize: '0.7rem',
                            flexShrink: 0,
                          }}>
                            {isAutoGenerated ? 'A' : (report.cashier_name || '?').charAt(0).toUpperCase()}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{
                              color: '#e8eaf6', fontWeight: 600, fontSize: '0.82rem',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {report.cashier_name || '—'}
                            </Typography>
                            {isAutoGenerated && (
                              <Typography sx={{
                                color: '#fb923c', fontWeight: 700, fontSize: '0.6rem',
                                textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1,
                              }}>
                                Auto
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Total Sales */}
                      <Grid item sm={2}>
                        <Typography sx={{
                          fontWeight: 800, color: '#00C896', fontSize: '0.95rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          letterSpacing: '-0.3px',
                        }}>
                          €{(fs.total_sales || 0).toFixed(2)}
                        </Typography>
                      </Grid>

                      {/* Orders */}
                      <Grid item sm={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 42, height: 28, px: 1, borderRadius: '8px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            color: '#e8eaf6', fontWeight: 700, fontSize: '0.82rem',
                          }}>
                            {fs.total_orders || 0}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Cash Difference */}
                      <Grid item sm={1.5}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          px: 1, py: 0.4, borderRadius: '7px',
                          backgroundColor: `${diffAccent}15`,
                          border: `1px solid ${diffAccent}35`,
                        }}>
                          <Typography sx={{
                            fontWeight: 700, fontSize: '0.76rem', color: diffAccent,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          }}>
                            {diff === 0 ? '€0.00' : `${diff > 0 ? '+' : ''}€${diff.toFixed(2)}`}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Status */}
                      <Grid item sm={1}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                            px: 1, py: 0.45, borderRadius: '999px',
                            backgroundColor: statusIsFinal ? 'rgba(0, 200, 150,0.12)' : 'rgba(245,158,11,0.12)',
                            border: `1px solid ${statusIsFinal ? 'rgba(0, 200, 150,0.35)' : 'rgba(245,158,11,0.35)'}`,
                          }}>
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%',
                              backgroundColor: statusIsFinal ? '#00C896' : '#f59e0b',
                              boxShadow: `0 0 0 3px ${statusIsFinal ? 'rgba(0, 200, 150,0.25)' : 'rgba(245,158,11,0.25)'}`,
                              animation: !statusIsFinal ? 'pulse 2s ease-in-out infinite' : 'none',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.4 },
                              },
                            }} />
                            <Typography sx={{
                              fontWeight: 700, fontSize: '0.68rem',
                              color: statusIsFinal ? '#00C896' : '#fbbf24',
                              textTransform: 'uppercase', letterSpacing: '0.4px',
                            }}>
                              {report.status}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Actions */}
                      <Grid item sm={3}>
                        <Box sx={{
                          display: 'flex', gap: 0.4, justifyContent: 'flex-end',
                          p: 0.4, borderRadius: '10px',
                          backgroundColor: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.04)',
                          width: 'fit-content', marginLeft: 'auto',
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedReport(normalizeReport(report)); setViewReportDialog(true); }}
                            sx={{
                              width: 30, height: 30, borderRadius: '7px',
                              color: '#60a5fa', backgroundColor: 'rgba(96,165,250,0.08)',
                              '&:hover': { backgroundColor: '#60a5fa', color: '#0f1117' },
                            }}
                            title={t('daily_closing.view_details')}
                          >
                            <Assessment sx={{ fontSize: 15 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenResetDialog(report)}
                            sx={{
                              width: 30, height: 30, borderRadius: '7px',
                              color: '#fb923c', backgroundColor: 'rgba(251,146,60,0.08)',
                              '&:hover': { backgroundColor: '#fb923c', color: '#0f1117' },
                            }}
                            title={t('daily_closing.reset_title')}
                          >
                            <Refresh sx={{ fontSize: 15 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handlePrintPreview(report)}
                            sx={{
                              width: 30, height: 30, borderRadius: '7px',
                              color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.08)',
                              '&:hover': { backgroundColor: '#00C896', color: '#0f1117' },
                            }}
                            title={t('daily_closing.print_preview')}
                          >
                            <LocalPrintshop sx={{ fontSize: 15 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => exportReportToPDF(normalizeReport(report))}
                            sx={{
                              width: 30, height: 30, borderRadius: '7px',
                              color: '#a78bfa', backgroundColor: 'rgba(167,139,250,0.08)',
                              '&:hover': { backgroundColor: '#a78bfa', color: '#0f1117' },
                            }}
                            title={t('daily_closing.export_pdf')}
                          >
                            <PictureAsPdf sx={{ fontSize: 15 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReport(report.id || '')}
                            sx={{
                              width: 30, height: 30, borderRadius: '7px',
                              color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)',
                              '&:hover': { backgroundColor: '#ef4444', color: '#fff' },
                            }}
                            title={t('common.delete') + ' ' + t('daily_closing.title')}
                          >
                            <Delete sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      {/* Reset Report Dialog */}
      {resetDialogOpen && resetTargetReport && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setResetDialogOpen(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.12)', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('daily_closing.reset_title')}
              </Typography>
              <IconButton
                onClick={() => setResetDialogOpen(false)}
                size="small"
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('daily_closing.reset_description')}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {t('daily_closing.date_cashier')}
                </Typography>
                <Typography variant="body2">
                  {new Date(resetTargetReport.report_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')} • {resetTargetReport.cashier_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('daily_closing.reset_reason')}
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder={t('daily_closing.reset_reason_placeholder')}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setResetDialogOpen(false)}
                  startIcon={<Close />}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleConfirmReset}
                  startIcon={<Refresh />}
                >
                  {t('daily_closing.reset_save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Generate Report Dialog - Custom Modal (Same Style as Customer Information Modal) */}
      {generateReportDialog && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            // Only close if clicking the overlay, not the dialog content
            if (e.target === e.currentTarget) {
              setGenerateReportDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('daily_closing.generate_report_title')}
              </Typography>
              <IconButton
                onClick={() => setGenerateReportDialog(false)}
                size="small"
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Report Date */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('daily_closing.report_date')}
                  </Typography>
                  <TextField
                    type="date"
                    value={reportForm.reportDate}
                    onChange={(e) => setReportForm(prev => ({ ...prev, reportDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Cashier Name - Auto-populated from logged-in user */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('daily_closing.cashier_name')}
                  </Typography>
                  <TextField
                    value={reportForm.cashierName}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#2d3048',
                        cursor: 'not-allowed',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                        '&.Mui-disabled': {
                          color: '#9ca3af',
                        },
                      },
                    }}
                    helperText={currentUserInfo ? `Logged in as: ${currentUserInfo.email || currentUserInfo.name}` : 'Loading user information...'}
                  />
                </Box>

                {/* Opening Cash */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('daily_closing.opening_cash')}
                  </Typography>
                  <TextField
                    type="number"
                    value={reportForm.openingCash}
                    onChange={(e) => setReportForm(prev => ({ ...prev, openingCash: parseFloat(e.target.value) || 0 }))}
                    fullWidth
                    placeholder="0.00"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Actual Cash Count */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('daily_closing.actual_cash')}
                  </Typography>
                  <TextField
                    type="number"
                    value={reportForm.actualCash}
                    onChange={(e) => setReportForm(prev => ({ ...prev, actualCash: parseFloat(e.target.value) || 0 }))}
                    fullWidth
                    placeholder="0.00"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setGenerateReportDialog(false)}
                sx={{
                  color: '#9ca3af',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={generateDailyReport}
                variant="contained"
                disabled={loading || !reportForm.cashierName || !currentUserInfo}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                    color: '#9ca3af',
                  },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('daily_closing.generate')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* View Report Dialog - Custom Modal (Same Style as Customer Information Modal) */}
      {viewReportDialog && selectedReport && (
        <Box
          data-modal-overlay="true"
          onClick={() => setViewReportDialog(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{
              bgcolor: '#2d3048',
              p: 2,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('daily_closing.view_details')}
              </Typography>
              <IconButton
                onClick={() => setViewReportDialog(false)}
                size="small"
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content - Scrollable */}
            <Box sx={{
              p: 3,
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0, // Critical for flex scrolling
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#2d3048',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}>
              {/* Financial Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                  💰 {t('daily_closing.financial_summary')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.total_sales')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.financial_summary?.total_sales ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.orders')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.financial_summary?.total_orders ?? 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.total_customers')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.financial_summary?.total_customers ?? 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.average_order_value')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.financial_summary?.average_order_value ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  {(selectedReport.financial_summary as any).discounts_given > 0 && (
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                      {language === 'ar' ? 'Rabatte gesamt' : 'Total Discounts'}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #ffcc80',
                      borderRadius: 1,
                      backgroundColor: 'rgba(255, 152, 0, 0.12)',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#e65100' }}>
                        -{(selectedReport.financial_summary as any).discounts_given.toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  )}
                </Grid>
              </Box>

              {/* Cash Reconciliation */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                  💵 {t('daily_closing.cash_reconciliation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.opening_cash_label')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.cash_reconciliation?.opening_cash ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.cash_sales')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.cash_reconciliation?.cash_sales ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.expected_cash')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.cash_reconciliation?.expected_cash ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      {t('daily_closing.actual_cash_label')}
                    </Typography>
                    <Box sx={{
                      p: 1.5,
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedReport.cash_reconciliation?.actual_cash ?? 0).toFixed(2)} EUR
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('daily_closing.cash_difference')}
                  </Typography>
                  <Box sx={{
                    p: 1.5,
                    border: `1px solid ${(selectedReport.cash_reconciliation?.cash_difference ?? 0) === 0 ? '#4caf50' : '#ff9800'}`,
                    borderRadius: 1,
                    backgroundColor: (selectedReport.cash_reconciliation?.cash_difference ?? 0) === 0 ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 152, 0, 0.12)',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: (selectedReport.cash_reconciliation?.cash_difference ?? 0) === 0 ? '#4caf50' : '#ff9800'
                      }}
                    >
                      {(selectedReport.cash_reconciliation?.cash_difference ?? 0).toFixed(2)} EUR
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Top Selling Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                  🏆 {t('daily_closing.top_selling_items')}
                </Typography>
                {(selectedReport?.sales_analysis?.top_selling_items || []).length > 0 ? ((selectedReport?.sales_analysis?.top_selling_items || []).slice(0, 5).map((item, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #3a3d52', borderRadius: 1, backgroundColor: '#252838' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: appTheme.theme.button_background_color || '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600
                      }}>
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                          {item.quantity}x - {(item.revenue || 0).toFixed(2)} EUR ({(item.percentage_of_sales || 0).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))) : (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>Keine Daten verfügbar</Typography>
                )}
              </Box>

              {/* Hourly Sales Chart */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                  📊 {t('daily_closing.hourly_sales')}
                </Typography>
                <Box sx={{ p: 2, border: '1px solid #3a3d52', borderRadius: 1, backgroundColor: '#252838' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsBarChart data={selectedReport.sales_analysis?.hourly_breakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => [`${value} EUR`, 'Revenue']} />
                      <Bar dataKey="revenue" fill={appTheme.theme.button_background_color || '#1976d2'} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Employee Performance - NEW: Shows each employee separately */}
              {selectedReport.employee_performance && selectedReport.employee_performance.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                    👥 {t('daily_closing.employee_performance')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedReport.employee_performance.map((employee, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2.5,
                          border: '1px solid #3a3d52',
                          borderRadius: 2,
                          backgroundColor: '#252838',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderColor: '#1976d2',
                          },
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#9ca3af' }}>
                              {t('daily_closing.employee_label')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.employee_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                              {employee.employee_email}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#9ca3af' }}>
                              {t('daily_closing.orders')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.orders_created}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#9ca3af' }}>
                              {t('daily_closing.sales_label')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#64b5f6' }}>
                              {employee.total_sales.toFixed(2)} EUR
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#9ca3af' }}>
                              {t('daily_closing.hours_label')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.hours_worked.toFixed(1)}h
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#9ca3af' }}>
                              {t('daily_closing.avg_order')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {employee.average_order_value.toFixed(2)} EUR
                            </Typography>
                          </Grid>
                        </Grid>
                        {employee.first_order_time && employee.last_order_time && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #3a3d52' }}>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                              {t('daily_closing.work_period')}: {new Date(employee.first_order_time).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })} - {new Date(employee.last_order_time).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setViewReportDialog(false)}
                sx={{
                  color: '#9ca3af',
                  textTransform: 'none',
                  border: '1px solid #3a3d52',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {t('common.close')}
              </Button>
              <Button
                onClick={() => selectedReport && printReport(selectedReport)}
                variant="contained"
                startIcon={<Print />}
                sx={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                  },
                }}
              >
                {t('daily_closing.print_report')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Print Preview Dialog */}
      {printPreviewDialog && previewReport && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPrintPreviewDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('daily_closing.print_preview')} - {new Date(previewReport.report_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
              </Typography>
              <IconButton
                onClick={() => setPrintPreviewDialog(false)}
                size="small"
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Preview Content */}
            <Box sx={{ p: 3, overflowY: 'auto', flex: 1, backgroundColor: '#2d3048' }}>
              <Box sx={{
                backgroundColor: '#252838',
                p: 3,
                borderRadius: 2,
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                border: '1px solid #3a3d52',
                maxHeight: '60vh',
                overflow: 'auto'
              }}>
                {generatePrintContent(previewReport, language).split('\n').map((line, index) => {
                  // Remove ESC/POS commands for display
                  const cleanLine = line
                    .replace(/\x1B\[[0-9;]*m/g, '') // Remove ANSI escape codes
                    .replace(/\x1B./g, '') // Remove ESC commands
                    .replace(/\[LOGO_PLACEHOLDER\]/g, '[LOGO]');
                  return (
                    <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px', lineHeight: 1.4 }}>
                      {cleanLine || ' '}
                    </Typography>
                  );
                })}
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setPrintPreviewDialog(false)}
                sx={{
                  color: '#9ca3af',
                  textTransform: 'none',
                  border: '1px solid #3a3d52',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => previewReport && printReport(previewReport)}
                variant="contained"
                startIcon={<Print />}
                sx={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
              >
                {t('daily_closing.print_report')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Zwischenbericht Preview Dialog */}
      {zwischenberichtPreviewDialog && previewZwischenbericht && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setZwischenberichtPreviewDialog(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #3a3d52',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Zwischenbericht Vorschau - {new Date(previewZwischenbericht.report_date).toLocaleDateString('en-US')}
              </Typography>
              <IconButton
                onClick={() => setZwischenberichtPreviewDialog(false)}
                size="small"
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Preview Content */}
            <Box sx={{ p: 3, overflowY: 'auto', flex: 1, backgroundColor: '#2d3048' }}>
              <Box
                sx={{
                  backgroundColor: '#252838',
                  padding: 4,
                  borderRadius: 2,
                  fontFamily: '"Courier New", monospace',
                  fontSize: '16px',
                  lineHeight: 1.9,
                  whiteSpace: 'pre-wrap',
                  border: '1px solid #3a3d52',
                  maxWidth: '500px',
                  margin: '0 auto',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                {generateZwischenberichtPreviewText(previewZwischenbericht)
                  .split('\n')
                  .map((line, index) => {
                    // Use the clean preview text (no ESC/POS commands)
                    let cleanLine = line;
                    
                    // Handle empty lines
                    if (!cleanLine || cleanLine.trim().length === 0) {
                      cleanLine = ' '; 
                    }
                    
                    // Determine if line should be bold or centered
                    const isBold = cleanLine === 'SAHEEH' ||
                                  cleanLine.includes('KASSENABSCHLUSSBELEG') ||
                                  cleanLine.includes('BAR ZAHLUNGEN') ||
                                  cleanLine.includes('ALLE ZAHLUNGEN') ||
                                  cleanLine.includes('BONUSSYSTEM') ||
                                  cleanLine === 'EINNAHMEN' ||
                                  cleanLine === 'ZAHLARTEN' ||
                                  cleanLine === 'KASSENVORGÄNGE' ||
                                  cleanLine.includes('Vorbestand') ||
                                  cleanLine.includes('Einnahmen') ||
                                  cleanLine.includes('Ausgaben') ||
                                  cleanLine.includes('Endbestand') ||
                                  cleanLine.includes('Gesamt') ||
                                  (cleanLine.includes('Umsatz') && cleanLine.includes('%')) ||
                                  cleanLine.includes('Einnahmen') ||
                                  cleanLine.includes('Ausgaben') ||
                                  cleanLine.includes('Endbestand') ||
                                  cleanLine.includes('Gesamt Netto') ||
                                  cleanLine.includes('Gesamt:') ||
                                  cleanLine.includes('Zahlarten:') ||
                                  cleanLine.includes('Kassenvorgänge:');
                    
                    const isCenter = cleanLine.includes('[LOGO]') ||
                                    cleanLine.includes('Äußere Kanalstraße') ||
                                    cleanLine.includes('Steuernummer') ||
                                    cleanLine.trim() === '50827 Köln' ||
                                    cleanLine.includes('KASSENABSCHLUSSBELEG');
                    
                    const isSeparator = cleanLine.startsWith('─') || cleanLine.startsWith('-');
                    
                    return (
                      <Typography
                        key={index}
                        component="div"
                        sx={{
                          fontFamily: '"Courier New", monospace',
                          fontSize: cleanLine.includes('[LOGO]') ? '18px' : cleanLine === 'KASSENABSCHLUSSBELEG' ? '20px' : isBold ? '16px' : '14px',
                          fontWeight: isBold ? 'bold' : 'normal',
                          lineHeight: isSeparator ? 1.2 : 1.8,
                          whiteSpace: 'pre-wrap',
                          textAlign: isCenter ? 'center' : 'left',
                          marginBottom: isSeparator ? '8px' : '2px',
                          marginTop: isSeparator ? '8px' : '0',
                          color: isSeparator ? '#999' : '#000',
                          borderBottom: isSeparator ? '1px solid #ddd' : 'none',
                          paddingBottom: isSeparator ? '4px' : '0',
                        }}
                      >
                        {cleanLine || '\u00A0'}
                      </Typography>
                    );
                  })}
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setZwischenberichtPreviewDialog(false)}
                sx={{
                  color: '#9ca3af',
                  textTransform: 'none',
                  border: '1px solid #3a3d52',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Schließen
              </Button>
              <Button
                onClick={async () => {
                  if (previewZwischenbericht) {
                    await exportZwischenberichtToPDF(previewZwischenbericht);
                  }
                }}
                variant="outlined"
                startIcon={<FileDownload />}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  },
                }}
              >
                Als PDF speichern
              </Button>
              <Button
                onClick={() => {
                  if (previewZwischenbericht) {
                    printZwischenbericht(previewZwischenbericht);
                  }
                }}
                variant="contained"
                startIcon={<Print />}
                sx={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  },
                }}
              >
                Drucken
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DailyClosingReportsPage;

